// SDM — IN INFINITUM · Chrome : la matière de la collection Chrome, pour l'îlot en ambiance « Chrome »
// (sections/sdm-ilot.liquid) et la fiche du gabarit produit « chrome » (templates/product.chrome.json).
//
// Sept pièces, qui se branchent seules, y compris dans la fiche en calque, insérée après coup par
// assets/sdm-fiche.js (d'où ce module chargé sur toutes les pages par layout/theme.liquid) :
// - <sdm-chrome-fond> : un chrome liquide en noir et blanc, en WebGL, fixe derrière la section ou la fiche : le shader
//   de « Liquid Metal Prototype » (Lucidity), à l'identique hormis la couleur. Il ne coule qu'avec les gestes du
//   visiteur — le défilement, le pointeur —, presque immobile sinon. C'est lui qui branche les pièces suivantes dans sa
//   section ou sa fiche ;
// - le volet cannelé de l'îlot ([data-sdm-chrome-volet], la tête du cartel) : des bandes de verre posées en haut de
//   la section, comme une image, par-dessus le chrome — la couleur du dessus, par paliers de moins en moins présents,
//   un flou qui baisse de bande en bande, et la réfraction d'une lentille à chaque arête (en WebGL, dans la tête) ;
// - en option, l'ouverture « 8 à plat » de l'îlot ([data-sdm-huit]), un trompe-l'œil : le labyrinthe du site, vu au
//   ras des murs ; la caméra en fait un quart de tour et se relève — au cœur des boucles, les deux logos en médailles
//   de chrome (assets/sdm-chrome-huit.js, chargé à l'approche) ;
// - le bouton chromé du cartel, en option ([data-sdm-chrome-bouton]) : une pilule de chrome liquide, en WebGL, à sa
//   taille (par défaut, le bouton est en fond uni, comme ceux de la fiche) ;
// - le verre liquide de l'expérience (assets/sdm-glass.js) sur les boutons en verre ([data-sdm-verre]) et un encart,
//   posé par le module, sous la galerie de la fiche ; sur les cases de l'îlot et la carte sous les pièces, sa
//   réfraction refaite en WebGL, sur le chrome du fond (tous navigateurs, Safari sur iPhone compris) ;
// - la fenêtre d'une pièce de l'îlot, au clic, comme le menu burger : vues numérotées, désignation, prix, courte
//   description, un ou deux boutons (Maps, fiche, Click & Collect) et la page « Être informé » (inscription sans
//   quitter la fenêtre).
// - <sdm-huit-cotes> : le 8 aux deux bords de la fiche Chrome, redessiné en WebGL pour onduler comme un métal liquide,
//   avec sa lueur (sinon, ses calques CSS).
//
// Mouvement réduit, économie de données ou animation coupée dans la section (data-fixe) : une image fixe. Hors de
// l'écran, ou sous la fiche ouverte en calque, rien n'est dessiné.

const REDUIT = matchMedia('(prefers-reduced-motion: reduce)').matches || !!navigator.connection?.saveData;
const IMAGE_MS = 1000 / 30; // un mouvement aussi lent n'a pas besoin de plus de 30 images par seconde
const REPOS_MS = 1000 / 10; // sans geste : le liquide ne fait plus que dériver, à peine
// la cadence se compte en images de l'écran : une image sur deux (sur six au repos), toujours la même. Comparé tout
// juste à 33 ms, l'écart entre deux images de l'écran (16,7 ms, à un rien près) passait une fois sur deux, une fois
// sur trois : le liquide avançait par saccades (33, 50, 33 ms…)
const MARGE_MS = 4;
const DERIVE = 0.04; // la vitesse du liquide au repos, rapportée à celle de la référence : presque immobile
const PIXELS_MAX = 2e6; // plafond du fond, en pixels dessinés
// la définition des toiles du chrome (fond, volet, verre des cases), en px par px CSS : 1,5 au plus — les filets de
// lumière de la référence restent fins ; 1 sur un écran tactile (un téléphone : plus de deux fois moins de pixels à
// calculer, à chaque image, pour un liquide qui ne s'y voit pas moins net)
const DEFINITION = matchMedia('(pointer: coarse)').matches ? 1 : 1.5;
const MOBILE = matchMedia('(max-width: 860px)'); // la mise en page mobile de l'îlot (assets/sdm-chrome.css)

// La hauteur de l'écran, stable : 100lvh. Sur iPhone, innerHeight change quand la barre d'adresse se replie ou revient ;
// le liquide, qui coule avec le défilement compté en hauteurs d'écran, sautait alors d'autant (plus loin dans la page,
// plus fort), et le calque des cases changeait de taille.
let sondeEcran = null;
let hauteurEcran = 0;
function hauteurStable() {
  if (!hauteurEcran) {
    if (!sondeEcran && document.body) {
      sondeEcran = document.createElement('div');
      sondeEcran.setAttribute('aria-hidden', 'true');
      sondeEcran.style.cssText = 'position:fixed;top:0;left:0;width:0;height:100vh;height:100lvh;visibility:hidden;pointer-events:none';
      document.body.append(sondeEcran);
    }
    hauteurEcran = sondeEcran?.offsetHeight || innerHeight;
  }
  return hauteurEcran;
}
addEventListener('resize', () => (hauteurEcran = 0), { passive: true });

// fond (et repli), ombre et lumière, en RVB de 0 à 1. Sombre : les gris de la référence tels quels (à 100 %
// d'intensité). Clair : les mêmes, éclaircis — ses noirs deviennent un gris d'argent, sous un gamma qui les étale.
const TONS = {
  clair: { fond: [0.945, 0.945, 0.95], ombre: [0.5, 0.505, 0.52], lumiere: [1, 1, 1], gamma: 0.7 },
  sombre: { fond: [0.035, 0.035, 0.04], ombre: [0, 0, 0], lumiere: [1, 1, 1], gamma: 1 },
};

const SOMMETS = `attribute vec2 aPos;
varying vec2 vUv;
void main() { vUv = aPos * 0.5 + 0.5; gl_Position = vec4(aPos, 0.0, 1.0); }`;

// Le fond : le shader de la référence, « Liquid Metal Prototype v0.1 » (proto.lucidity.design), à l'identique —
// une marche de rayons dans un champ ondulé, éclairée par sa pente —, rendu en niveaux de gris. Seuls ajouts : un
// cadrage centré (celui de la référence sur un écran 16:10, quelle que soit la forme de la toile), tourné d'un quart
// de tour en portrait — un écran de téléphone en montre alors toute la hauteur, aussi dense que sur un écran large,
// au lieu d'une tranche étroite et lisse —, le défilement qui fait couler le temps, le pointeur qui décale à peine la
// vue. uCadre place le pixel dans la toile du fond (× xy, + zw) : l'identité pour le fond ; pour le volet, qui dessine
// le même chrome sous la tête du cartel, la place de la tête dans l'écran.
// L'empreinte de la coulée (l'ouverture « 8 à plat », assets/sdm-chrome-huit.js, qui en tient les valeurs : fond.trace) :
// une image de l'écran figé de la coulée (uTrace : r, l'arrivée du mercure, rapportée à uTraceV.y ; g, les murs ; b et a,
// le sens de la coulée), placée par uTraceR (depuis le pixel du fond, × xy, + zw). Là où le mercure est passé depuis peu
// (uTraceV.x : son front, en m), le liquide garde la forme des murs qu'il a brûlés — des veines, dans le champ même — et
// son motif est entraîné dans le sens de la coulée ; le tout se détend à mesure que le front avance, puis s'efface
// (uTraceV.z : la force). Recopié à l'identique dans le mercure (LIQUIDE) : la scène et le fond restent une seule image.
const FOND = `precision highp float;
uniform vec4 uCadre;
uniform vec2 uRes;
uniform float uTemps;
uniform vec3 uPointeur;
uniform float uIntensite;
uniform vec3 uFond;
uniform vec3 uOmbre;
uniform vec3 uLumiere;
uniform float uGamma;
uniform sampler2D uTrace;
uniform vec4 uTraceV;
uniform vec4 uTraceR;
mat2 m(float a) { float c = cos(a), s = sin(a); return mat2(c, -s, s, c); }
float map(vec3 p, float t) {
  p.xz *= m(1.3);
  p.xy *= m(0.4);
  vec3 q = p * 3.5 + t;
  return p.x * p.y * length(p + vec3(sin(1.7))) * log(length(p) + 1.0) + sin(q.x + sin(q.z + sin(q.y))) * 0.7 - 1.0;
}
vec3 trace(vec2 st) {
  if (uTraceV.z <= 0.0) return vec3(0.0);
  vec4 tr = texture2D(uTrace, st);
  float tau = uTraceV.x - tr.r * uTraceV.y;
  if (tau <= 0.0) return vec3(0.0);
  float vif = uTraceV.z * exp(-tau / 9.0) * smoothstep(0.0, 0.6, tau);
  return vec3((vec2(tr.b, 1.0 - tr.a) * 2.0 - 1.0) * vif, tr.g * vif);
}
void main() {
  float t = uTemps;
  vec2 fc = gl_FragCoord.xy * uCadre.xy + uCadre.zw;
  vec3 e = trace(fc * uTraceR.xy + uTraceR.zw);
  vec2 q = fc - 0.5 * uRes + e.xy * 0.035 * min(uRes.x, uRes.y);
  vec2 pt = uPointeur.xy - 0.5;
  if (uRes.y > uRes.x) { q = vec2(-q.y, q.x); pt = vec2(-pt.y, pt.x); }
  vec2 uv = q / min(uRes.x, uRes.y) + vec2(-0.2, -0.3) + pt * 0.06 * uPointeur.z;
  vec3 cl = vec3(0.8);
  float d = 5.5;
  for (int i = 0; i <= 5; i++) {
    vec3 p = vec3(0.0, 0.2, 6.0) + normalize(vec3(uv, -3.0)) * d;
    float rz = map(p, t) - 0.9 * e.z;
    float f = clamp((rz - map(p + 0.1, t)) * 0.5 * cos(t * 0.2) * p.x, -0.1, 0.5);
    vec3 l = vec3(0.1, 0.2, 0.4) + vec3(0.1, 2.5, 2.0) * f;
    cl = cl * l + (1.2 - smoothstep(0.8, 2.5, rz)) * 0.7 * l;
    d += min(rz, 0.8);
  }
  float g = pow(dot(clamp(cl, 0.0, 1.0), vec3(0.2126, 0.7152, 0.0722)), uGamma);
  vec3 c = mix(uFond, mix(uOmbre, uLumiere, g), uIntensite);
  c += (fract(sin(dot(fc, vec2(12.9898, 78.233))) * 43758.5453) - 0.5) / 255.0;
  gl_FragColor = vec4(c, 1.0);
}`;

// Le bouton chromé du cartel (en option) : le premier effet (MetalliCSS — une pilule de chrome, claire en haut, une
// onde sombre dans le bas), refait au propre. La pilule est galbée (plate au centre, ronde sur les bords : une
// super-ellipse) ; sa surface ondule doucement, avec le champ liquide du prototype « cannelé v4 » ; elle reflète le
// studio de ce même prototype, en gris (ciel, horizon net, sol sombre, une ligne de lumière, un bandeau vertical),
// comme une sphère réfléchissante (sa normale lit le studio), et son reflet spéculaire. Le studio est relevé pour que
// le texte tombe sur le ciel et l'horizon sous lui. 4 échantillons par pixel : plus de crénelage dans l'onde. Un
// filet sombre cerne la pilule, pour qu'elle se détache aussi sur un fond blanc.
const METAL = `precision highp float;
uniform vec2 uTaille;
uniform float uEchelle;
uniform float uTemps;
uniform vec2 uInclinaison;
uniform float uVue;
varying vec2 vUv;
float hg(vec2 p, float t) {
  vec2 q = p * 1.6;
  q += 0.45 * vec2(sin(q.y * 1.7 + t * 1.3), cos(q.x * 1.4 - t));
  q += 0.25 * vec2(sin(q.y * 3.1 - t * 1.7), cos(q.x * 2.9 + t * 1.1));
  return 0.5 * sin(q.x * 1.3 + t) + 0.5 * cos(q.y * 1.1 - t * 0.8) + 0.25 * sin((q.x + q.y) * 2.3 + t * 1.5);
}
float env(vec2 q) {
  float y = q.y + 0.15 * q.x;
  float ciel = mix(0.6, 0.96, smoothstep(0.0, 0.7, y));
  float sol = mix(0.16, 0.34, clamp(-y / 0.7, 0.0, 1.0));
  float c = mix(sol, ciel, smoothstep(-0.04, 0.04, y));
  c += 0.45 * (1.0 - smoothstep(0.0, 0.07, abs(y - 0.42)));
  c += 0.18 * (1.0 - smoothstep(0.0, 0.12, abs(q.x - 0.5))) * step(0.0, y);
  return c;
}
float chrome(vec2 px) {
  vec2 demi = uTaille * 0.5;
  float r = demi.y;
  vec2 q = vec2(sign(px.x) * max(abs(px.x) - (demi.x - r), 0.0), px.y);
  float lq = length(q);
  float t = min(lq / r, 0.999);
  float pente = pow(t, 1.6) / pow(1.0 - pow(t, 2.6), 0.615);
  vec2 dir = lq > 0.0 ? q / lq : vec2(0.0);
  vec3 n = normalize(vec3(dir * pente, 1.0));
  vec2 lp = px / (uTaille.y * 1.2);
  float tt = uTemps * 0.25;
  float h = hg(lp, tt);
  vec2 g = vec2(hg(lp + vec2(0.02, 0.0), tt) - h, hg(lp + vec2(0.0, 0.02), tt) - h) / 0.02;
  n = normalize(n - vec3(g * 0.06, 0.0));
  float l = env(n.xy * 0.95 + vec2(0.0, 0.42 + uVue) + uInclinaison * vec2(0.08, 0.1));
  vec3 hv = normalize(normalize(vec3(-0.4, 0.5, 1.0)) + vec3(0.0, 0.0, 1.0));
  l += pow(max(dot(n, hv), 0.0), 120.0) * 0.8;
  l *= 1.0 - 0.35 * (1.0 - smoothstep(0.0, 1.4, (1.0 - t) * r * uEchelle));
  return l;
}
void main() {
  vec2 px = (vUv - 0.5) * uTaille;
  float d = 0.25 / uEchelle;
  float l = 0.25 * (chrome(px + vec2(-d, -d)) + chrome(px + vec2(d, -d)) + chrome(px + vec2(-d, d)) + chrome(px + vec2(d, d)));
  gl_FragColor = vec4(vec3(clamp(l, 0.0, 1.0)), 1.0);
}`;

/* ---------- WebGL ---------- */

function contexte(canvas, options) {
  try {
    return canvas.getContext('webgl', options) || canvas.getContext('experimental-webgl', options);
  } catch {
    return null;
  }
}

/** Programme d'un triangle plein écran ; `null` si le pilote refuse le shader (le repli CSS reste). */
function programme(gl, source) {
  const shader = (type, src) => {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) console.warn('[sdm] chrome :', gl.getShaderInfoLog(s));
    return s;
  };
  const prog = gl.createProgram();
  gl.attachShader(prog, shader(gl.VERTEX_SHADER, SOMMETS));
  gl.attachShader(prog, shader(gl.FRAGMENT_SHADER, source));
  gl.bindAttribLocation(prog, 0, 'aPos'); // le même attribut pour tous les programmes d'un contexte
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;
  gl.useProgram(prog);
  gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  const u = {};
  return { p: prog, u: (nom) => (u[nom] ??= gl.getUniformLocation(prog, nom)) };
}

/** Les réglages d'une image du fond, pour son programme ou celui du volet (`cadre` : uCadre). */
function poserFond(gl, prog, v, cadre = [1, 1, 0, 0]) {
  gl.uniform4fv(prog.u('uCadre'), cadre);
  gl.uniform2fv(prog.u('uRes'), v.res);
  gl.uniform1f(prog.u('uTemps'), v.temps);
  gl.uniform3fv(prog.u('uPointeur'), v.pointeur);
  gl.uniform1f(prog.u('uIntensite'), v.intensite);
  gl.uniform3fv(prog.u('uFond'), v.ton.fond);
  gl.uniform3fv(prog.u('uOmbre'), v.ton.ombre);
  gl.uniform3fv(prog.u('uLumiere'), v.ton.lumiere);
  gl.uniform1f(prog.u('uGamma'), v.ton.gamma);
  // l'empreinte de la coulée : son image (unité 1), sa place — du pixel du fond (px de sa toile, depuis le bas) au
  // point de l'image (px CSS, depuis le haut de sa zone)
  const tr = v.trace;
  gl.uniform4fv(prog.u('uTraceV'), tr ? tr.V : [0, 0, 0, 0]);
  if (!tr) return;
  imageTrace(gl, tr);
  gl.uniform1i(prog.u('uTrace'), 1);
  const k = v.res[0] / v.css[0];
  const [l, h, w, hz] = tr.zone;
  gl.uniform4f(prog.u('uTraceR'), 1 / (k * w), -1 / (k * hz), -l / w, (v.css[1] - h) / hz);
}

// l'image de l'empreinte, une texture par contexte, rechargée quand elle change (version)
const traces = new WeakMap();
function imageTrace(gl, tr) {
  let e = traces.get(gl);
  if (!e) traces.set(gl, (e = { version: null, tex: gl.createTexture() }));
  gl.activeTexture(gl.TEXTURE1);
  gl.bindTexture(gl.TEXTURE_2D, e.tex);
  if (e.version !== tr.version) {
    e.version = tr.version;
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, tr.w, tr.h, 0, gl.RGBA, gl.UNSIGNED_BYTE, tr.data);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  }
  gl.activeTexture(gl.TEXTURE0);
}

/** Une couleur de réglage (#rrvvbb) en RVB de 0 à 1 ; du blanc sinon. */
function rvb(hex) {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '');
  return m ? m.slice(1).map((c) => parseInt(c, 16) / 255) : [1, 1, 1];
}

/* ---------- pointeur, partagé ---------- */

const pointeur = { x: -1, y: -1, quand: -Infinity };
addEventListener(
  'pointermove',
  (e) => {
    const course = pointeur.quand > -Infinity ? Math.hypot(e.clientX - pointeur.x, e.clientY - pointeur.y) : 0;
    elan.pointeur = Math.min(4, elan.pointeur + course / 500);
    pointeur.x = e.clientX;
    pointeur.y = e.clientY;
    pointeur.quand = performance.now();
  },
  { passive: true },
);

// L'élan : ce que le visiteur fait bouger. Le pointeur et le défilement le nourrissent (en distance parcourue), il
// retombe en une demi-seconde. C'est lui qui fait couler le liquide : au repos, presque rien ne bouge.
const elan = { pointeur: 0, defil: 0, t: performance.now() };
const positions = new WeakMap();
addEventListener(
  'scroll',
  (e) => {
    const el = e.target === document ? document.scrollingElement : e.target;
    const y = el?.scrollTop ?? 0;
    const avant = positions.get(el);
    positions.set(el, y);
    if (avant !== undefined) elan.defil = Math.min(4, elan.defil + Math.abs(y - avant) / 400);
  },
  { passive: true, capture: true },
);
function lireElan(maintenant) {
  const amorti = Math.exp(-Math.max(0, maintenant - elan.t) / 1000 / 0.18);
  elan.t = maintenant;
  elan.pointeur *= amorti;
  elan.defil *= amorti;
  return elan;
}
// la vitesse du liquide, rapportée à celle de la référence : presque nulle au repos ; le pointeur qui traverse
// franchement l'écran la porte à celle de la référence, et jusqu'à trois fois plus
const vitesse = (e) => DERIVE + Math.min(3, 2.5 * e.pointeur + 0.6 * e.defil);
const calme = (e) => e.pointeur + e.defil < 0.02;

// sous la fiche ouverte en calque (assets/sdm-fiche.js), qui recouvre la page : inutile de dessiner
const masque = (el) => document.documentElement.classList.contains('sdm-fiche-open') && !el.closest('.sdm-fiche-layer');

/* ---------- le fond ---------- */

/**
 * La toile est fixe, de la taille de la fenêtre : le chrome ne bouge pas avec la page, le fond le rogne à la section
 * (clip-path, assets/sdm-chrome.css). Une image par tranche de 33 ms tant que le visiteur bouge — le pointeur, lissé,
 * et le défilement, lu à chaque image (la page ne défile pas toujours sur window : Horizon fait défiler
 * .page-wrapper sur ordinateur) —, une par tranche de 100 ms sinon ; une seule, fixe, en mouvement réduit.
 */
class SdmChromeFond extends HTMLElement {
  gl = null;
  prog = null;
  canvas = null;
  temps = 0;
  defil = 0; // place du fond dans la fenêtre, en hauteurs d'écran
  lisse = { x: 0.5, y: 0.5, force: 0 }; // pointeur dans la toile (0-1, y vers le haut)
  valeurs = null; // les réglages de la dernière image (poserFond), repris par le volet
  // () => vrai quand l'ouverture « 8 à plat » couvre l'écran ; « valeurs » quand elle le couvre mais en reprend les
  // valeurs (le mercure) : elles avancent, sans image (esquisse : les valeurs n'ont pas encore été dessinées)
  couvert = null;
  esquisse = false;
  // l'empreinte de la coulée, tenue par l'ouverture : { data, w, h, version, zone : [gauche, haut, largeur, hauteur] en
  // px CSS sur la toile, V : [front, étendue des arrivées, force, 0] }
  trace = null;
  images = 0; // nombre d'images dessinées
  suiveurs = new Set(); // appelés après chaque image : le volet redessine le chrome sous lui
  nettoyage = [];
  #visible = false;
  #raf = 0;
  #dernier = 0;
  // Sous la coulée de l'ouverture (couvert : « valeurs », 26/10 — demande JB : la coulée « fait vibrer l'écran ») : les
  // valeurs avancent à chaque image de l'écran, comme le front du mercure qui les montre — à 30 par seconde, son
  // liquide avançait par à-coups sous un front fluide —, et seulement avec les gestes : au repos, sans la dérive, le
  // mercure ne change plus, l'ouverture n'a plus d'image à recalculer (avant : dix par seconde, à l'arrêt).
  #sousCoulee = false;

  get fixe() {
    return REDUIT || this.hasAttribute('data-fixe');
  }

  connectedCallback() {
    if (this.gl) return;
    // les boutons et les verres de la section ou de la fiche, une fois tout leur contenu en place (avec ou sans WebGL)
    requestAnimationFrame(() => {
      const portee = this.closest('[data-sdm-chrome], .sdm-fiche--chrome') || this.parentElement;
      if (!portee) return;
      portee.querySelectorAll('[data-sdm-chrome-bouton]').forEach(chromer);
      if (portee.matches('.sdm-fiche--chrome')) galerie(portee);
      else {
        pieces(portee);
        teaser(portee);
        volets(portee, this);
        huit(portee, this);
      }
      if (portee.matches('.sdm-chrome--sombre')) {
        sommet(portee.querySelector('.sdm-ilot'));
        sommet(portee.querySelector('.sdm-chrome-pied'));
      }
      if (!portee.matches('.sdm-fiche--chrome')) cases(portee, this);
      verrer(portee);
    });
    const ecran = this.querySelector('.sdm-chrome-fond__ecran') || this;
    const canvas = document.createElement('canvas');
    canvas.className = 'sdm-chrome-fond__canvas';
    const gl = contexte(canvas, {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      powerPreference: 'low-power',
    });
    const prog = gl && programme(gl, FOND);
    if (!prog) return; // le dégradé de repli (assets/sdm-chrome.css) reste
    ecran.append(canvas);
    this.#brancher(canvas, gl, prog);
    if (this.dataset.entete) this.#entete();
  }

  disconnectedCallback() {
    this.nettoyage.forEach((f) => f());
    this.nettoyage = [];
    this.canvas?.remove();
    this.gl = null;
  }

  #brancher(canvas, gl, prog) {
    Object.assign(this, { canvas, gl, prog });
    const io = new IntersectionObserver(([e]) => {
      this.#visible = e.isIntersecting;
      this.#relancer();
    });
    io.observe(this);
    const ro = new ResizeObserver(() => {
      this.#dimensionner();
      this.#relancer(true);
    });
    ro.observe(canvas);
    const reprendre = () => this.#relancer();
    document.addEventListener('sdm:fiche-close', reprendre);
    canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      cancelAnimationFrame(this.#raf);
      this.#raf = 0;
      this.classList.remove('is-pret');
      this.gl = null;
    });
    this.nettoyage.push(() => {
      document.removeEventListener('sdm:fiche-close', reprendre);
      io.disconnect();
      ro.disconnect();
      cancelAnimationFrame(this.#raf);
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    });
  }

  #relancer(force = false) {
    if (!this.gl || !this.#visible || this.#raf || masque(this)) return;
    if (this.fixe) {
      if (force || !this.classList.contains('is-pret')) this.#image(performance.now());
      return;
    }
    this.#raf = requestAnimationFrame(this.#image);
  }

  #image = (maintenant) => {
    this.#raf = 0;
    if (!this.gl) return;
    const e = lireElan(maintenant);
    if (!this.fixe && this.#visible && !masque(this)) this.#raf = requestAnimationFrame(this.#image);
    if (!this.fixe && !this.#sousCoulee && this.#dernier && maintenant - this.#dernier < (calme(e) ? REPOS_MS : IMAGE_MS) - MARGE_MS) return;
    const couvert = !this.fixe && this.couvert?.();
    this.#sousCoulee = couvert === 'valeurs';
    if (couvert === true) return; // l'ouverture « 8 à plat » couvre l'écran : rien à dessiner dessous
    const dt = this.#dernier ? Math.min(0.1, (maintenant - this.#dernier) / 1000) : 0;
    this.#dernier = maintenant;

    // fond fixe (la fiche) : il lit le défilement de la fiche qui le porte
    const repere = this.classList.contains('sdm-chrome-fond--fixe') ? this.parentElement : this;
    this.defil = -repere.getBoundingClientRect().top / hauteurStable();
    if (!this.fixe) this.temps += dt * (this.#sousCoulee ? vitesse(e) - DERIVE : vitesse(e));

    const toile = this.canvas.getBoundingClientRect();
    const actif = maintenant - pointeur.quand < 1500;
    const cible = actif ? 1 - (maintenant - pointeur.quand) / 1500 : 0;
    if (toile.width && pointeur.quand > -Infinity) {
      const k = this.fixe ? 1 : 0.08;
      this.lisse.x += ((pointeur.x - toile.left) / toile.width - this.lisse.x) * k;
      this.lisse.y += (1 - (pointeur.y - toile.top) / toile.height - this.lisse.y) * k;
    }
    this.lisse.force += (cible - this.lisse.force) * 0.06;

    this.#poser();
    if (couvert) {
      this.esquisse = true;
      return;
    }
    this.#dessiner();
    this.classList.add('is-pret');
  };

  #dimensionner() {
    const { gl, canvas } = this;
    const { clientWidth: w, clientHeight: h } = canvas;
    // à la définition de l'écran (DEFINITION au plus)
    let k = Math.min(devicePixelRatio || 1, DEFINITION);
    if (w * h * k * k > PIXELS_MAX) k = Math.sqrt(PIXELS_MAX / (w * h));
    canvas.width = Math.max(1, Math.round(w * k));
    canvas.height = Math.max(1, Math.round(h * k));
  }

  #poser() {
    const intensite = parseFloat(this.dataset.intensite);
    const tr = this.trace;
    this.valeurs = {
      res: [this.canvas.width, this.canvas.height],
      css: [this.canvas.clientWidth || 1, this.canvas.clientHeight || 1],
      // l'empreinte de la coulée, figée pour cette image (le mercure de l'ouverture la reprend telle quelle)
      trace: tr && tr.V[2] > 0 ? { ...tr, V: [...tr.V] } : null,
      // le temps de la référence (une demi-unité par seconde à sa vitesse) ; le défilement le fait couler d'autant
      temps: this.temps * 0.5 + (this.fixe ? 0 : this.defil),
      pointeur: [this.lisse.x, this.lisse.y, this.fixe ? 0 : this.lisse.force],
      intensite: Number.isFinite(intensite) ? Math.min(1, Math.max(0, intensite / 100)) : 1,
      ton: TONS[this.dataset.ton] || TONS.clair,
    };
  }

  #dessiner() {
    const { gl, prog, canvas } = this;
    this.esquisse = false;
    this.images++;

    gl.useProgram(prog.p);
    gl.viewport(0, 0, canvas.width, canvas.height);
    poserFond(gl, prog, this.valeurs);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    this.suiveurs.forEach((f) => f());
  }

  // Ton sombre : l'en-tête transparent passe en clair quand le chrome est sous lui, comme pour le push vidéo
  // (`data-sdm-entete` sur <body>, lu par la règle que snippets/sdm-chrome-fond.liquid écrit). Avec un volet, le
  // chrome ne domine qu'aux deux tiers de la tête du cartel : un repère couvre la section de là jusqu'en bas. Avec
  // l'ouverture « 8 à plat », l'îlot remonte sous la section d'avant et commence par le labyrinthe blanc : le repère part
  // du moment où le chrome couvre le haut de la scène (data-bascule : 76 % de sa course quand les logos se figent et
  // que le mercure brûle le labyrinthe, 92 % avec la plongée) — sans lui, l'en-tête passerait en clair sur le blanc de
  // la section d'avant.
  #entete() {
    const entete = document.querySelector('.sdm-header');
    if (!entete) return;
    const id = this.dataset.entete;
    const transparent = (c) => c === 'transparent' || /[,/]\s*0(\.0*)?\s*\)$/.test(c);
    const ilot = this.closest('[data-sdm-chrome]');
    const volet = ilot?.querySelector('[data-sdm-chrome-volet]:not([data-sens="bas"])');
    const huit = ilot?.querySelector('[data-sdm-huit]');
    // le volet en bas de l'îlot (en tête de page) : le repère s'arrête à mi-hauteur, où la couleur de la section
    // suivante l'emporte — l'en-tête y repasse à l'encre
    const queue = ilot?.querySelector('[data-sdm-chrome-volet][data-sens="bas"]');
    let cible = this;
    let placer = () => {};
    if (volet || huit) {
      cible = document.createElement('span');
      cible.className = 'sdm-chrome-fond__repere';
      this.append(cible);
      placer = volet
        ? () => (cible.style.top = `${volet.offsetTop + volet.offsetHeight * 0.7}px`)
        : () => {
            // (la course réglée ; ce que la carte du temps du module y ajoute : data-montee, en px ; la bascule sur
            // cette carte : data-bascule-u, en longueurs de course — sans elle, au prorata de la course). L'îlot
            // remonte d'un écran sous la section d'avant (dessous), sauf en tête de page : la course commence en haut.
            const scene = huit.querySelector('.sdm-huit__scene')?.offsetHeight || innerHeight;
            const montee = Number(huit.dataset.montee) || 0;
            const dessous = Math.max(0, -parseFloat(getComputedStyle(ilot).marginTop) || 0);
            const course = huit.offsetHeight - scene - dessous - montee;
            const bascule = Number(huit.dataset.bascule) || 0.92;
            const u = huit.dataset.basculeU ? Number(huit.dataset.basculeU) : bascule;
            const fin = course > 0 ? dessous + u * course : huit.offsetHeight;
            // le repère compte dès qu'il entre dans la bande de l'en-tête (100 px, la marge de l'observateur)
            cible.style.top = `${huit.offsetTop + fin + (course > 0 ? 100 : 0)}px`;
            // (jusqu'à ce que la moitié claire du volet du bas atteigne la ligne des icônes de l'en-tête, 37 px sous son
            // haut, barre d'annonce comprise : le repère s'arrête d'autant au-dessus du milieu du volet)
            if (queue) cible.style.bottom = `${Math.round(queue.offsetHeight * 0.5 + entete.getBoundingClientRect().top + 37)}px`;
          };
      placer();
    }
    // Le repère dans la bande de l'en-tête (les 100 px du haut de l'écran), lu à la place qu'il occupe, à chaque image
    // où la page défile — et seulement quand la section est à l'écran (un observateur sans marge). Le 26/10 : un
    // observateur réduit à cette bande par sa marge ne marchait pas dans l'aperçu de l'éditeur de thème (la page y est
    // tenue par un cadre d'un autre domaine, où la marge est ignorée) : l'en-tête passait en clair sur le labyrinthe
    // blanc, ou restait sombre sur le chrome (captures de JB)
    const body = document.body;
    let image = 0;
    const juger = () => {
      image = 0;
      const r = cible.getBoundingClientRect();
      if (r.height > 0 && r.top < 100 && r.bottom > 0 && transparent(getComputedStyle(entete).backgroundColor)) body.dataset.sdmEntete = id;
      else if (body.dataset.sdmEntete === id) delete body.dataset.sdmEntete;
    };
    const auPas = () => {
      if (!image) image = requestAnimationFrame(juger);
    };
    let ecoute = false;
    const ecouter = (oui) => {
      if (oui === ecoute) return;
      ecoute = oui;
      if (oui) document.addEventListener('scroll', auPas, { capture: true, passive: true });
      else document.removeEventListener('scroll', auPas, { capture: true });
      auPas();
    };
    const vue = new IntersectionObserver(([e]) => ecouter(e.isIntersecting));
    vue.observe(ilot || this);
    const redimensionner = () => {
      placer();
      auPas();
    };
    addEventListener('resize', redimensionner, { passive: true });
    const ro = (volet || huit) && new ResizeObserver(redimensionner);
    ro?.observe(this);
    this.nettoyage.push(() => {
      vue.disconnect();
      ecouter(false);
      cancelAnimationFrame(image);
      ro?.disconnect();
      removeEventListener('resize', redimensionner);
      if (cible !== this) cible.remove();
      if (body.dataset.sdmEntete === id) delete body.dataset.sdmEntete;
    });
  }
}

/* ---------- le verre liquide de l'expérience : cases, galerie, boutons ---------- */

/**
 * Fiche Chrome : la galerie sur un encart de verre, posé ici plutôt que dans sections/sdm-fiche.liquid (un décor,
 * sans contenu). En tête de la colonne des vues, en absolu sur toute sa hauteur avec un écran collant, comme le
 * grand chiffre des packs (assets/sdm-chrome.css) : les vues défilent par-dessus.
 */
function galerie(fiche) {
  const media = fiche.querySelector('.sdm-fiche-media');
  if (!media || media.querySelector(':scope > .sdm-fiche-verre')) return;
  media.insertAdjacentHTML(
    'afterbegin',
    `<div class="sdm-fiche-verre" aria-hidden="true">
      <div class="sdm-fiche-verre-fixe"><span class="sdm-fiche-verre-panneau" data-refract="lite" data-sdm-verre="galerie"></span></div>
      <svg width="0" height="0" class="sdm-chrome-defs"><defs data-sdm-chrome-verre></defs></svg>
    </div>`,
  );
}

// Le verre de la pilule de l'expérience, réglé pour chaque usage, sans chroma (des franges de couleur jureraient sur
// un chrome en noir et blanc). Cases et galerie : en version « lite » (un seul déplacement), comme les cartes
// produits du prototype, avec un bord bombé à l'échelle de grandes surfaces et un flou au cœur — léger pour les
// cases, plus fort sous la galerie de la fiche, pour calmer le chrome derrière la pièce. Boutons : les réglages du
// bouton en verre du push vidéo. Fenêtre des pièces (la carte et son bouton rond) : un verre plus profond que celui
// du menu burger — un large bord (56 px) qui courbe fortement la page, de légères franges de couleur sur ce seul bord,
// un flou modéré au cœur pour garder le texte lisible, des couleurs avivées. La teinte, le reflet et le liseré sont en CSS (assets/sdm-chrome.css), à la portée de l'îlot, de la fiche ou de la
// fenêtre : les variables globales du verre (--lg-*) restent celles de la pilule.
const VERRES = {
  case: { bezel: 36, curvature: 2.4, depth: 80, chroma: 0, blur: 2.5, saturate: 1 },
  galerie: { bezel: 44, curvature: 2.4, depth: 90, chroma: 0, blur: 7, saturate: 1 },
  bouton: { bezel: 18, curvature: 2.2, depth: 40, chroma: 0, blur: 0.8, saturate: 1 },
  fenetre: { bezel: 56, curvature: 2.6, depth: 140, chroma: 0.15, blur: 4, saturate: 1.6 },
};
const verres = new WeakSet();

async function verrer(portee) {
  const elements = [...portee.querySelectorAll('[data-sdm-verre]')].filter((el) => !verres.has(el));
  const defs = portee.querySelector('[data-sdm-chrome-verre]');
  if (!elements.length || !defs) return;
  elements.forEach((el) => verres.add(el));
  try {
    // carte d'import de layout/theme.liquid (snippets/sdm-importmap.liquid)
    const { createGlass, GLASS_DEFAULTS } = await import('sdm/glass');
    if (!portee.isConnected) return;
    for (const [type, params] of Object.entries(VERRES)) {
      const lot = elements.filter((el) => el.dataset.sdmVerre === type);
      if (!lot.length) continue;
      const verre = createGlass({ defs, params: { ...structuredClone(GLASS_DEFAULTS), ...params } });
      lot.forEach(verre.register);
      if (type !== 'bouton') qualite(verre, portee);
    }
  } catch (err) {
    // sans réfraction, les verres gardent leur flou, leur reflet et leur liseré en CSS
    console.warn('[sdm] verre liquide indisponible', err);
  }
}

/**
 * Qualité adaptative, comme dans le prototype : la réfraction des grands verres se recalcule à chaque image du
 * chrome liquide. Si la page passe sous 40 images par seconde à leur arrivée à l'écran, ils passent au flou seul.
 */
function qualite(verre, portee) {
  if (verre.resolveMode() !== 'backdrop') return;
  const io = new IntersectionObserver(([e]) => {
    if (!e.isIntersecting) return;
    io.disconnect();
    const ecarts = [];
    let avant = 0;
    const mesurer = (t) => {
      if (avant) ecarts.push(t - avant);
      avant = t;
      if (ecarts.length < 90) return requestAnimationFrame(mesurer);
      ecarts.sort((a, b) => a - b);
      if (ecarts[45] > 25) verre.setLiteBlur(true);
    };
    setTimeout(() => requestAnimationFrame(mesurer), 1000); // après l'apparition des pièces
  });
  io.observe(portee);
}

/* ---------- le volet de l'îlot ---------- */

// Le volet : comme une image posée en haut de la section, par-dessus le chrome liquide — des bandes de verre
// qui remplissent la tête du cartel, de son haut à son bas, en nombre entier (la largeur réglée des cannelures,
// arrondie pour que la dernière finisse au bas de la tête ; 60 % sur mobile). Chaque bande est uniforme : la couleur
// du dessus (blanc par défaut), presque pleine dans la première, de moins en moins présente d'une bande à l'autre, par
// paliers ; un flou fort dans la première, presque nul dans la dernière.
//
// Le verre est dessiné en WebGL, dans une toile posée dans la tête du cartel : elle défile avec la page, rien ne
// glisse entre les bandes et le cartel. La toile redessine le chrome du fond sous la tête — le même shader, aux mêmes
// réglages, à la même place de l'écran — puis le fait traverser par le verre. Pour tous les navigateurs qui ont
// WebGL, Safari sur iPhone compris. Sans WebGL : les bandes en CSS (repli, sans déformation). Le sur-titre du cartel
// est calé au milieu de la bande la plus proche.
//
// Le verre liquide (26/10 au soir, demande JB : « un vrai effet Liquid Glass ») : chaque bande est une dalle de verre
// au galbe d'un « squircle » convexe le long de ses deux arêtes (h(x) = ⁴√(1 − (1 − x)⁴), x de 0 à l'arête à 1 au
// bout du galbe, un tiers de la bande de chaque côté) et presque plate au milieu — le profil des reconstitutions du
// Liquid Glass d'Apple (kube.io). Le rayon qui la traverse est réfracté par la loi de Snell (verre d'indice 1,5) :
// au milieu, l'image passe presque droite, à peine grossie (une loupe de 4 %) ; dans le galbe, elle est tirée vers le
// cœur de la bande, de plus en plus près de l'arête, où elle se resserre, frangée de couleur (la dispersion : le rouge
// un peu plus dévié que le bleu). Les arêtes prennent la lumière d'en haut à gauche : un reflet spéculaire vif dans le
// galbe du haut (Blinn-Phong), un plus faible dans celui du bas (la lumière qui ressort après un rebond), plus fort à
// gauche qu'à droite ; leur éclat suit le chrome qu'elles reflètent (lu au-delà de l'arête) : vif devant ses filets de
// lumière, presque éteint devant ses noirs — des reflets par morceaux, qui glissent au défilement, au lieu d'un filet
// continu. Au bord, le verre reflète plus qu'il ne laisse passer (Fresnel, Schlick) ; le
// galbe du bas, à l'ombre, s'assombrit un peu. Le voile de la couleur du dessus s'éclaircit dans le galbe : même
// sur les bandes presque blanches, la courbure du verre se voit, comme une dalle de verre sur une page blanche. Le
// flou de la bande est retenu dans le galbe : la réfraction y reste lisible.
const VOLET_O = 0.08; // ce que la première bande laisse voir du chrome ; la dernière, presque tout
const VOLET_FLOU = 22; // repli CSS : le flou de la première bande, en pixels ; il baisse jusqu'à presque rien
const VOLET_PIXELS = 9e5; // plafond de la toile du volet, en pixels dessinés
const VOLET_DEFINITION = 2; // la toile du volet, en px par px CSS : les reflets des arêtes restent fins, iPhone compris

// Pour chaque pixel de la tête : sa bande (i), sa place dans la bande (f, de 0 en haut à 1 en bas), l'arête la plus
// proche (o : −1 en haut, +1 en bas, le long de nr) et sa distance au galbe (x) ; la normale du verre (pente du
// squircle × GALBE), le rayon réfracté (refract, 1 / 1,5), qui traverse l'épaisseur de la dalle jusqu'au chrome ; la
// lecture du chrome net, du flou moyen ou du flou fort, en trois canaux (dispersion) ; le voile, puis la lumière. Les
// arêtes sont adoucies sur un pixel de l'écran (la bande voisine s'y mêle).
const VOLET = `precision highp float;
uniform sampler2D uImage;
uniform sampler2D uFlou1;
uniform sampler2D uFlou2;
uniform vec2 uTaille;
uniform vec2 uEchelle;
uniform float uPas;
uniform float uAngle;
uniform vec3 uHaut;
uniform float uBas;
const float O = ${VOLET_O.toFixed(2)};
const float GALBE_LARGEUR = 0.33;
const float GALBE = 1.0;
const float PENTE_MAX = 6.0;
const float EPAISSEUR = 0.5;
const float LOUPE = 0.04;
const float DISPERSION = 0.08;
vec2 uvDe(vec2 q) { return vec2(q.x / uTaille.x, 1.0 - q.y / uTaille.y); }
vec3 lire(sampler2D t, vec2 r, vec2 g, vec2 b) { return vec3(texture2D(t, r).r, texture2D(t, g).g, texture2D(t, b).b); }
float lum(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }
float prDe(float i, vec2 nr, vec2 p, float u) {
  float q = clamp((p.y - nr.y * (u - (i + 0.5) * uPas)) / uTaille.y, 0.0, 1.0);
  return uBas > 0.5 ? 1.0 - q : q;
}
vec3 verre(vec2 p, float u, float i, vec2 nr) {
  float f = clamp(u / uPas - i, 0.0, 1.0);
  float o = f < 0.5 ? -1.0 : 1.0;
  float x = clamp(min(f, 1.0 - f) / GALBE_LARGEUR, 0.0, 1.0);
  float rim = 1.0 - x;
  float s4 = max(1.0 - pow(rim, 4.0), 1e-4);
  float h = pow(s4, 0.25);
  float pente = min(pow(rim, 3.0) / pow(s4, 0.75), PENTE_MAX) / (GALBE_LARGEUR * 2.0);
  vec3 n = normalize(vec3(nr * o * pente * GALBE, 1.0));
  vec3 t = refract(vec3(0.0, 0.0, -1.0), n, 1.0 / 1.5);
  vec2 dv = t.xy / max(-t.z, 0.25) * uPas * EPAISSEUR * (0.6 + 0.4 * h);
  dv -= nr * (u - (i + 0.5) * uPas) * LOUPE;
  vec2 dr = dv * (1.0 + DISPERSION * rim), db = dv * (1.0 - DISPERSION * rim);
  vec2 r = uvDe(p + dr), g = uvDe(p + dv), b = uvDe(p + db);
  float pr = prDe(i, nr, p, u);
  float tf = 2.0 * pow(1.0 - pr, 1.2) * (1.0 - 0.55 * rim);
  vec3 c = tf < 1.0 ? mix(lire(uImage, r, g, b), lire(uFlou1, r, g, b), tf) : mix(lire(uFlou1, r, g, b), lire(uFlou2, r, g, b), tf - 1.0);
  c = c * 1.03 + 0.012;
  float op = mix(O, 1.0, pow(pr, 1.6));
  op = mix(op, 1.0, 0.2 * rim * rim);
  c = mix(uHaut, c, op);
  vec3 V = vec3(0.0, 0.0, 1.0);
  vec3 L = normalize(vec3(-0.3, -0.8, 0.52));
  vec3 L2 = normalize(vec3(0.3, 0.8, 0.52));
  float env = lum(texture2D(uFlou1, uvDe(p + nr * o * uPas * 0.6)).rgb);
  float cote = mix(1.15, 0.75, clamp(p.x / uTaille.x, 0.0, 1.0));
  float spec = pow(max(dot(n, normalize(L + V)), 0.0), 32.0);
  float spec2 = pow(max(dot(n, normalize(L2 + V)), 0.0), 32.0);
  float eclat = 0.12 + 0.88 * smoothstep(0.2, 0.85, env);
  c += (0.55 * spec + 0.18 * spec2) * eclat * cote;
  float fres = 0.04 + 0.96 * pow(1.0 - n.z, 5.0);
  c = mix(c, vec3(min(mix(env, lum(uHaut), 0.5) * 1.1 + 0.1, 1.0)), 0.3 * fres);
  c *= 1.0 - 0.05 * rim * rim * step(0.0, o);
  return c;
}
void main() {
  vec2 p = vec2(gl_FragCoord.x / uEchelle.x, uTaille.y - gl_FragCoord.y / uEchelle.y);
  vec2 nr = vec2(sin(uAngle), cos(uAngle));
  float u = dot(p, nr);
  float i = floor(u / uPas);
  float f = u / uPas - i;
  vec3 col = verre(p, u, i, nr);
  float e0 = f * uPas * uEchelle.y;
  float e1 = (1.0 - f) * uPas * uEchelle.y;
  if (e0 < 1.0) col = mix(verre(p, u, i - 1.0, nr), col, 0.5 + 0.5 * e0);
  else if (e1 < 1.0) col = mix(verre(p, u, i + 1.0, nr), col, 0.5 + 0.5 * e1);
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
}`;

// Les flous du volet, au quart de la définition : une réduction (4 × 4 pixels moyennés), puis un flou gaussien à
// 9 points (5 lectures bilinéaires), passé dans le sens des cannelures à pas croissants et, plus court, en travers :
// le flou moyen (une quinzaine de pixels le long, une dizaine en travers), puis le flou fort (une quarantaine le
// long, une douzaine en travers).
const REDUIRE = `precision highp float;
uniform sampler2D uTex;
uniform vec2 uTexel;
varying vec2 vUv;
void main() {
  vec3 c = texture2D(uTex, vUv + uTexel * vec2(-1.0, -1.0)).rgb + texture2D(uTex, vUv + uTexel * vec2(1.0, -1.0)).rgb;
  c += texture2D(uTex, vUv + uTexel * vec2(-1.0, 1.0)).rgb + texture2D(uTex, vUv + uTexel * vec2(1.0, 1.0)).rgb;
  gl_FragColor = vec4(c * 0.25, 1.0);
}`;

const FLOU = `precision highp float;
uniform sampler2D uTex;
uniform vec2 uPas;
varying vec2 vUv;
void main() {
  vec3 c = texture2D(uTex, vUv).rgb * 0.227027;
  c += (texture2D(uTex, vUv + uPas * 1.384615).rgb + texture2D(uTex, vUv - uPas * 1.384615).rgb) * 0.316216;
  c += (texture2D(uTex, vUv + uPas * 3.230769).rgb + texture2D(uTex, vUv - uPas * 3.230769).rgb) * 0.070270;
  gl_FragColor = vec4(c, 1.0);
}`;

// La tête à volet, et, en tête de page, le volet en bas de l'îlot (data-sens « bas ») : les mêmes bandes, retournées
// — la couleur de la section voisine en bas, le chrome net en haut.
function volets(portee, fond) {
  for (const tete of portee.querySelectorAll('[data-sdm-chrome-volet]')) {
    const pile = tete.querySelector('.sdm-volets__pile');
    if (!pile || pile.dataset.pret) continue;
    pile.dataset.pret = '';
    const largeur = parseFloat(tete.dataset.pas) || 56;
    // en biais (réglage « Angle des cannelures »)
    const angle = parseFloat(tete.dataset.angle) || 0;
    const repli = () => bandes(tete, pile, largeur, angle);
    if (!fond?.gl || !toileVolet(tete, fond, largeur, angle, repli)) repli();
  }
}

// le nombre de bandes dans une tête de hauteur h
const nombreBandes = (h, largeur) => Math.max(2, Math.round(h / (largeur * (MOBILE.matches ? 0.6 : 1))));

/**
 * La toile du volet : son propre contexte WebGL, dans la tête du cartel. À chaque image du fond, et à chaque image de
 * l'écran quand la page défile (la tête a bougé sous le chrome fixe), elle redessine le chrome sous la tête (le
 * programme du fond, cadré sur la tête), ses deux flous, puis le verre. `false` si WebGL refuse ; si le contexte est
 * perdu en route, la toile part et le repli CSS prend sa place.
 */
function toileVolet(tete, fond, largeur, angle, repli) {
  const hote = tete.querySelector('.sdm-volets');
  const canvas = document.createElement('canvas');
  canvas.className = 'sdm-volets__toile';
  const gl = hote && contexte(canvas, { alpha: false, antialias: false, depth: false, stencil: false, powerPreference: 'low-power' });
  const p = gl && { chrome: programme(gl, FOND), reduire: programme(gl, REDUIRE), flou: programme(gl, FLOU), verre: programme(gl, VOLET) };
  if (!p || !Object.values(p).every(Boolean)) return false;
  hote.prepend(canvas);
  const cible = () => ({ tex: gl.createTexture(), fbo: gl.createFramebuffer() });
  const t = { net: cible(), a: cible(), b: cible(), c: cible(), d: cible() };
  const haut = rvb(tete.dataset.couleur);
  const bas = tete.dataset.sens === 'bas';
  const rad = (angle * Math.PI) / 180;
  const etat = { w: 0, h: 0, qw: 1, qh: 1, pas: 1, pret: false, visible: false, perdu: false, avant: '', raf: 0 };

  const dimensionner = () => {
    const { clientWidth: w, clientHeight: h } = canvas;
    if (!w || !h) return;
    let k = Math.min(devicePixelRatio || 1, VOLET_DEFINITION);
    if (w * h * k * k > VOLET_PIXELS) k = Math.sqrt(VOLET_PIXELS / (w * h));
    canvas.width = Math.max(1, Math.round(w * k));
    canvas.height = Math.max(1, Math.round(h * k));
    etat.qw = Math.ceil(canvas.width / 4);
    etat.qh = Math.ceil(canvas.height / 4);
    etat.pret = [
      [t.net, canvas.width, canvas.height],
      [t.a, etat.qw, etat.qh],
      [t.b, etat.qw, etat.qh],
      [t.c, etat.qw, etat.qh],
      [t.d, etat.qw, etat.qh],
    ].every(([c, w2, h2]) => {
      gl.bindTexture(gl.TEXTURE_2D, c.tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w2, h2, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      for (const [nom, val] of [
        [gl.TEXTURE_MIN_FILTER, gl.LINEAR],
        [gl.TEXTURE_MAG_FILTER, gl.LINEAR],
        [gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE],
        [gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE],
      ])
        gl.texParameteri(gl.TEXTURE_2D, nom, val);
      gl.bindFramebuffer(gl.FRAMEBUFFER, c.fbo);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, c.tex, 0);
      return gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
    });
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.bindTexture(gl.TEXTURE_2D, null);
    const n = nombreBandes(h, largeur);
    Object.assign(etat, { w, h, pas: h / n, avant: '' });
    caler(tete, h / n, n);
  };

  const dessiner = () => {
    const v = fond.valeurs;
    if (etat.perdu || !etat.pret || !etat.visible || !v || !fond.canvas || masque(tete)) return;
    const r = canvas.getBoundingClientRect();
    const f = fond.canvas.getBoundingClientRect();
    if (!r.width || !r.height || !f.width || !f.height) return;
    const cle = `${fond.images}|${r.left.toFixed(1)}|${r.top.toFixed(1)}|${canvas.width}x${canvas.height}`;
    if (cle === etat.avant) return;
    etat.avant = cle;
    const { width: cw, height: ch } = canvas;
    const kx = v.res[0] / f.width;
    const ky = v.res[1] / f.height;

    // 1. le chrome sous la tête, tel que le fond le montre à cette place de l'écran
    gl.bindFramebuffer(gl.FRAMEBUFFER, t.net.fbo);
    gl.viewport(0, 0, cw, ch);
    gl.useProgram(p.chrome.p);
    poserFond(gl, p.chrome, v, [(kx * r.width) / cw, (ky * r.height) / ch, (r.left - f.left) * kx, (f.bottom - r.bottom) * ky]);
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    // 2. les flous : réduction au quart, puis des passes le long des cannelures (le) à pas croissants et en travers
    // (tr) ; le flou moyen dans d, le flou fort dans a. Les pas suivent la définition de la toile : les mêmes flous,
    // en pixels de la page, sur tous les écrans.
    const { qw, qh } = etat;
    const e = cw / etat.w / 1.5;
    gl.activeTexture(gl.TEXTURE0);
    gl.viewport(0, 0, qw, qh);
    gl.useProgram(p.reduire.p);
    gl.bindFramebuffer(gl.FRAMEBUFFER, t.a.fbo);
    gl.bindTexture(gl.TEXTURE_2D, t.net.tex);
    gl.uniform1i(p.reduire.u('uTex'), 0);
    gl.uniform2f(p.reduire.u('uTexel'), 1 / cw, 1 / ch);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.useProgram(p.flou.p);
    gl.uniform1i(p.flou.u('uTex'), 0);
    const le = [Math.cos(rad) / qw, Math.sin(rad) / qh]; // dans la texture, y vers le haut
    const tr = [Math.sin(rad) / qw, -Math.cos(rad) / qh];
    for (const [de, vers, sens, pas] of [
      [t.a, t.b, le, 1],
      [t.b, t.c, le, 2],
      [t.c, t.d, tr, 1.5],
      [t.d, t.b, le, 4],
      [t.b, t.c, tr, 2.5],
      [t.c, t.a, le, 8],
    ]) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, vers.fbo);
      gl.bindTexture(gl.TEXTURE_2D, de.tex);
      gl.uniform2f(p.flou.u('uPas'), sens[0] * pas * e, sens[1] * pas * e);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    // 3. le verre
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, cw, ch);
    gl.useProgram(p.verre.p);
    gl.bindTexture(gl.TEXTURE_2D, t.net.tex);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, t.d.tex);
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, t.a.tex);
    const u = p.verre.u;
    gl.uniform1i(u('uImage'), 0);
    gl.uniform1i(u('uFlou1'), 1);
    gl.uniform1i(u('uFlou2'), 2);
    gl.uniform2f(u('uTaille'), etat.w, etat.h);
    gl.uniform2f(u('uEchelle'), cw / etat.w, ch / etat.h);
    gl.uniform1f(u('uPas'), etat.pas);
    gl.uniform1f(u('uAngle'), rad);
    gl.uniform3fv(u('uHaut'), haut);
    gl.uniform1f(u('uBas'), bas ? 1 : 0);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    for (const unite of [gl.TEXTURE2, gl.TEXTURE1, gl.TEXTURE0]) {
      gl.activeTexture(unite);
      gl.bindTexture(gl.TEXTURE_2D, null);
    }
    canvas.classList.add('is-pret');
  };

  // la page défile : la tête bouge sous le chrome fixe, une image par image de l'écran
  const suivre = () => {
    if (etat.raf || !etat.visible) return;
    etat.raf = requestAnimationFrame(() => {
      etat.raf = 0;
      dessiner();
    });
  };
  const io = new IntersectionObserver(([en]) => {
    etat.visible = en.isIntersecting;
    dessiner();
  });
  io.observe(tete);
  const ro = new ResizeObserver(() => {
    dimensionner();
    dessiner();
  });
  ro.observe(canvas);
  const mobile = () => {
    dimensionner();
    dessiner();
  };
  const reprendre = () => {
    etat.avant = '';
    dessiner();
  };
  fond.suiveurs.add(dessiner);
  addEventListener('scroll', suivre, { passive: true, capture: true });
  MOBILE.addEventListener('change', mobile);
  document.addEventListener('sdm:fiche-close', reprendre);
  const nettoyer = () => {
    fond.suiveurs.delete(dessiner);
    removeEventListener('scroll', suivre, { capture: true });
    MOBILE.removeEventListener('change', mobile);
    document.removeEventListener('sdm:fiche-close', reprendre);
    io.disconnect();
    ro.disconnect();
    cancelAnimationFrame(etat.raf);
  };
  fond.nettoyage.push(() => {
    nettoyer();
    gl.getExtension('WEBGL_lose_context')?.loseContext();
  });
  canvas.addEventListener('webglcontextlost', (e) => {
    e.preventDefault();
    etat.perdu = true;
    nettoyer();
    canvas.remove();
    repli();
  });
  return true;
}

/**
 * Le repli, sans WebGL : les bandes en CSS (assets/sdm-chrome.css), une couleur et un flou par bande, uniformes ;
 * dessous, la bande floutée de moitié ; dessus, son flou entier, qui s'efface vers les arêtes. Sans déformation.
 */
function bandes(tete, pile, largeur, angle) {
  if (angle) {
    // en biais : la pile tourne, plus grande que la tête, qui la rogne
    pile.classList.add('sdm-volets__pile--biais');
    pile.style.setProperty('--sdm-volet-angle', `${angle}deg`);
  }
  let avant = '';
  const poser = () => {
    const { clientWidth: w, clientHeight: h } = pile;
    const n = nombreBandes(h, largeur);
    const cle = `${n}/${w}/${h}`;
    if (!w || !h || cle === avant) return;
    avant = cle;
    const bas = tete.dataset.sens === 'bas';
    pile.replaceChildren(
      ...Array.from({ length: n }, (_, i) => {
        // la place du milieu de la bande dans la tête, de 0 (couleur du dessus) à 1 (chrome net) ; retournée en bas
        const pr = bas ? 1 - (i + 0.5) / n : (i + 0.5) / n;
        const bande = document.createElement('span');
        bande.className = 'sdm-volet';
        bande.style.setProperty('--sdm-volet-blanc', (1 - VOLET_O - (1 - VOLET_O) * Math.pow(pr, 1.6)).toFixed(3));
        bande.style.setProperty('--sdm-volet-flou', `${(VOLET_FLOU * Math.pow(1 - pr, 1.2)).toFixed(1)}px`);
        bande.innerHTML = '<span class="sdm-volet__arete"></span><span class="sdm-volet__coeur"></span>';
        return bande;
      }),
    );
    caler(tete, h / n, n);
  };
  new ResizeObserver(poser).observe(pile);
  MOBILE.addEventListener('change', poser);
}

// Le sur-titre du cartel au milieu d'une bande : le cartel descend (ou monte) d'autant, et la tête garde sa hauteur
// (la marge sous le cartel rend ce que la marge au-dessus prend : assets/sdm-chrome.css, --sdm-volet-calage) — les
// bandes ne changent donc pas
function caler(tete, bh, n) {
  const surtitre = tete.querySelector('.sdm-cartel > .sdm-small');
  if (!surtitre) return;
  // sa place dans la tête, en mise en page (sans l'animation d'apparition du cartel)
  let haut = 0;
  let el = surtitre;
  while (el && el !== tete) {
    haut += el.offsetTop;
    el = el.offsetParent;
  }
  if (el !== tete) return;
  const avant = parseFloat(tete.style.getPropertyValue('--sdm-volet-calage')) || 0;
  const milieu = haut + surtitre.offsetHeight / 2 - avant;
  const k = Math.min(n - 1, Math.max(0, Math.round(milieu / bh - 0.5)));
  tete.style.setProperty('--sdm-volet-calage', `${((k + 0.5) * bh - milieu).toFixed(1)}px`);
}

/* ---------- le verre des cases, en WebGL ---------- */

// Les cases de l'îlot et la carte sous les pièces ([data-sdm-verre="case"]) : la réfraction du verre liquide en WebGL,
// comme le volet — pour tous les navigateurs qui ont WebGL, Safari sur iPhone compris (le filtre SVG du verre de
// l'expérience, en backdrop-filter, n'y marche que dans Chromium sur ordinateur : ailleurs, un flou seul). Le chrome sous
// les cases est connu : c'est le shader du fond. Un seul calque WebGL, dans la page, sous toutes les cases (dans leur
// bloc commun, en z-index −1). À chaque image du fond, il en redessine le chrome dans une texture, à la taille de
// l'écran et au-delà de ses bords, à 50 % de sa définition, légèrement flouté. À chaque image de l'écran où une case a
// bougé, il se recale sur l'écran et y dessine, pour chaque case, le verre : la lentille d'un rectangle arrondi à sa
// taille — un bord bombé, de plus en plus pentu vers l'arête, qui y tire l'image de l'intérieur (le profil du verre de
// l'expérience : son bord, sa courbure, sa profondeur, VERRES.case) —, la lumière qui se rassemble près des arêtes ;
// transparent autour. Le calque défile avec les cases, sans retard, et la lentille suit le défilement image par image ;
// rien n'est recopié : la toile WebGL s'affiche telle quelle (une toile 2D par case, où l'image WebGL était recopiée,
// obligeait Safari à relire chaque image de la carte graphique — c'est ce qui ralentissait l'écran). Rien n'est calculé
// tant qu'aucune case n'est à l'écran. Sans WebGL, ou si le contexte est perdu : le verre de l'expérience (verrer).
const CASES_ECHELLE = 0.5; // la définition du chrome sous les cases (× la taille CSS)
const CASES_MARGE = 48; // le chrome dessiné au-delà de ce que couvre le calque (px CSS) : la lentille va l'y chercher
const CASES_DEBORD = 0.25; // le calque déborde de l'écran en haut et en bas (× sa hauteur) : un défilement vif n'en sort pas
const CASES_PIXELS = 1.6e6; // plafond du calque, en pixels

// uCase : le coin haut gauche de la case dans le calque (px du calque, depuis le haut), la définition du calque (px
// par px CSS), sa hauteur (px) ; uBoite : la case dans la texture du chrome (son coin, la taille de la texture, en px
// CSS). Le bord de la case est adouci sur un pixel : le calque est transparent autour (alpha prémultiplié)
const VERRE_CASE = `precision highp float;
uniform sampler2D uChrome;
uniform vec4 uBoite;
uniform vec4 uCase;
uniform vec2 uTaille;
uniform float uRayon;
uniform vec3 uLentille;
uniform float uOpacite;
void main() {
  vec2 p = vec2(gl_FragCoord.x - uCase.x, uCase.w - gl_FragCoord.y - uCase.y) / uCase.z;
  vec2 c = p - 0.5 * uTaille;
  vec2 q = abs(c) - (0.5 * uTaille - uRayon);
  float sdf = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - uRayon;
  float a = clamp(0.5 - sdf * uCase.z, 0.0, 1.0) * uOpacite;
  float t = clamp(1.0 + sdf / uLentille.x, 0.0, 1.0);
  float m = pow(t, uLentille.y);
  vec2 n = (q.x > 0.0 && q.y > 0.0) ? normalize(q) : (q.x > q.y ? vec2(1.0, 0.0) : vec2(0.0, 1.0));
  n *= vec2(c.x < 0.0 ? -1.0 : 1.0, c.y < 0.0 ? -1.0 : 1.0);
  vec2 s = p - 0.5 * uLentille.z * m * n;
  vec2 uv = vec2((uBoite.x + s.x) / uBoite.z, 1.0 - (uBoite.y + s.y) / uBoite.w);
  vec3 col = texture2D(uChrome, uv).rgb;
  gl_FragColor = vec4(col * (1.0 + 0.14 * m * m) * a, a);
}`;

const casesVues = new Map(); // case → { visible, rayon, voile : ce qui la fait apparaître (.sdm-reveal) }
let casesCalque = null;

function cases(portee, fond) {
  if (!fond?.gl) return;
  const lot = [...portee.querySelectorAll('[data-sdm-verre="case"]')].filter((el) => !verres.has(el));
  if (!lot.length) return;
  const C = (casesCalque ??= calqueCases(fond, portee, lot));
  if (!C) return;
  for (const el of lot) {
    verres.add(el); // le verre de l'expérience la laisse : le calque prend sa place
    casesVues.set(el, { visible: false, rayon: 0, voile: el.closest('.sdm-reveal') });
    C.io.observe(el);
    C.ro.observe(el);
  }
}

// le bloc commun des cases, où le calque se glisse sous elles (au-dessus de la grille des pièces, dont le module de
// l'ouverture compte les enfants)
function hoteCases(lot) {
  let h = lot[0].parentElement;
  while (h && !lot.every((el) => h.contains(el))) h = h.parentElement;
  return h?.matches('.sdm-ilot') ? h.parentElement : h;
}

/** Le calque des cases : sa toile WebGL, ses programmes, ses textures ; null si WebGL refuse. */
function calqueCases(fond, portee, lot) {
  const hote = hoteCases(lot);
  if (!hote) return null;
  const canvas = document.createElement('canvas');
  canvas.className = 'sdm-verre-calque';
  canvas.setAttribute('aria-hidden', 'true');
  const gl = contexte(canvas, { alpha: true, premultipliedAlpha: true, antialias: false, depth: false, stencil: false, powerPreference: 'low-power' });
  const p = gl && { chrome: programme(gl, FOND), flou: programme(gl, FLOU), verre: programme(gl, VERRE_CASE) };
  if (!p || !Object.values(p).every(Boolean)) return null;
  // le calque se place dans le bloc (son coin, en absolu) et sous ses cases (son propre empilement)
  if (getComputedStyle(hote).position === 'static') hote.style.position = 'relative';
  hote.style.isolation = 'isolate';
  hote.append(canvas);
  const cible = () => ({ tex: gl.createTexture(), fbo: gl.createFramebuffer(), w: 0, h: 0 });
  const t = { a: cible(), b: cible() };
  const C = { gl, canvas, raf: 0, perdu: false, cleChrome: '', cle: '', ty: null, w: 0, h: 0, k: 1, debord: 0 };
  const taille = (c, w, h) => {
    if (c.w === w && c.h === h) return true;
    Object.assign(c, { w, h });
    gl.bindTexture(gl.TEXTURE_2D, c.tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    for (const [nom, val] of [
      [gl.TEXTURE_MIN_FILTER, gl.LINEAR],
      [gl.TEXTURE_MAG_FILTER, gl.LINEAR],
      [gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE],
      [gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE],
    ])
      gl.texParameteri(gl.TEXTURE_2D, nom, val);
    gl.bindFramebuffer(gl.FRAMEBUFFER, c.fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, c.tex, 0);
    const ok = gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return ok;
  };

  // la taille du calque : la largeur du bloc, la hauteur de l'écran (stable) et ses débords (pas plus que le bloc), à
  // la définition de l'écran (DEFINITION au plus, CASES_PIXELS au plus) ; refaite au changement de taille
  const dimensionner = () => {
    const H = hauteurStable();
    C.debord = Math.round(H * CASES_DEBORD);
    const w = hote.clientWidth;
    const h = Math.min(hote.clientHeight, H + 2 * C.debord);
    let k = Math.min(devicePixelRatio || 1, DEFINITION);
    if (w * h * k * k > CASES_PIXELS) k = Math.sqrt(CASES_PIXELS / Math.max(1, w * h));
    if (w === C.w && h === C.h && k === C.k) return;
    Object.assign(C, { w, h, k, cle: '' });
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    canvas.width = Math.max(1, Math.round(w * k));
    canvas.height = Math.max(1, Math.round(h * k));
  };

  // 1. le chrome, à la taille de la toile du fond et au-delà (débord du calque, marge de la lentille), puis un flou
  // léger (le flou du verre des cases)
  const zone = (f) => {
    const mx = CASES_MARGE, my = C.debord + CASES_MARGE;
    return { mx, my, x: f.left - mx, y: f.top - my, w: f.width + 2 * mx, h: f.height + 2 * my };
  };
  const chrome = (v, f) => {
    const z = zone(f);
    const tw = Math.max(1, Math.round(z.w * CASES_ECHELLE));
    const th = Math.max(1, Math.round(z.h * CASES_ECHELLE));
    if (!taille(t.a, tw, th) || !taille(t.b, tw, th)) return false;
    const sx = tw / z.w, sy = th / z.h;
    const kx = v.res[0] / f.width, ky = v.res[1] / f.height;
    gl.disable(gl.SCISSOR_TEST);
    gl.bindFramebuffer(gl.FRAMEBUFFER, t.a.fbo);
    gl.viewport(0, 0, tw, th);
    gl.useProgram(p.chrome.p);
    poserFond(gl, p.chrome, v, [kx / sx, ky / sy, -z.mx * kx, -z.my * ky]);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.useProgram(p.flou.p);
    gl.activeTexture(gl.TEXTURE0);
    gl.uniform1i(p.flou.u('uTex'), 0);
    const pas = (VERRES.case.blur * CASES_ECHELLE) / 1.6; // l'écart type du flou, en texels
    for (const [de, vers, x, y] of [
      [t.a, t.b, pas / tw, 0],
      [t.b, t.a, 0, pas / th],
    ]) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, vers.fbo);
      gl.bindTexture(gl.TEXTURE_2D, de.tex);
      gl.uniform2f(p.flou.u('uPas'), x, y);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return true;
  };

  // 2. le calque recalé sur l'écran (dans le bloc), effacé, puis le verre de chaque case à l'écran
  const dessiner = () => {
    const v = fond.valeurs;
    if (!v || !fond.canvas?.isConnected || !C.w || !C.h) return;
    const f = fond.canvas.getBoundingClientRect();
    if (!f.width || !f.height) return;
    const hr = hote.getBoundingClientRect();
    const ox = hr.left + hote.clientLeft, oy = hr.top + hote.clientTop;
    const ty = Math.round(Math.min(Math.max(0, -oy - C.debord), Math.max(0, hote.clientHeight - C.h)));
    const haut = oy + ty; // le haut du calque, à l'écran
    const vues = [];
    let cle = `${ty}|${C.w}x${C.h}|${ox.toFixed(1)}|${haut.toFixed(1)}`;
    for (const [el, c] of casesVues) {
      if (!c.visible || masque(el)) continue;
      const r = el.getBoundingClientRect();
      if (!r.width || !r.height || r.bottom < haut || r.top > haut + C.h) continue;
      const o = c.voile ? Number(getComputedStyle(c.voile).opacity) : 1;
      if (o <= 0.002) continue;
      vues.push([el, c, r, o]);
      cle += `|${r.left.toFixed(1)},${r.top.toFixed(1)},${r.width.toFixed(1)},${r.height.toFixed(1)},${o.toFixed(3)}`;
    }
    const cleChrome = `${fond.images}|${v.temps.toFixed(5)}|${f.left.toFixed(1)}|${f.top.toFixed(1)}|${f.width}x${f.height}|${C.debord}`;
    if (vues.length && cleChrome !== C.cleChrome) {
      if (!chrome(v, f)) return;
      C.cleChrome = cleChrome;
      C.cle = '';
    }
    if (cle === C.cle) return;
    C.cle = cle;
    if (C.ty !== ty) {
      C.ty = ty;
      canvas.style.transform = `translate3d(0, ${ty}px, 0)`;
    }
    const { k } = C;
    const cw = canvas.width, ch = canvas.height;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, cw, ch);
    gl.disable(gl.SCISSOR_TEST);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    if (!vues.length) return;
    const z = zone(f);
    const u = p.verre.u;
    const L = VERRES.case;
    gl.useProgram(p.verre.p);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, t.a.tex);
    gl.uniform1i(u('uChrome'), 0);
    gl.uniform3f(u('uLentille'), L.bezel, L.curvature, L.depth);
    gl.enable(gl.SCISSOR_TEST);
    for (const [el, c, r, o] of vues) {
      const x = (r.left - ox) * k, y = (r.top - haut) * k;
      const x0 = Math.max(0, Math.floor(x)), x1 = Math.min(cw, Math.ceil(x + r.width * k));
      const y0 = Math.max(0, Math.floor(ch - y - r.height * k)), y1 = Math.min(ch, Math.ceil(ch - y));
      if (x1 <= x0 || y1 <= y0) continue;
      gl.scissor(x0, y0, x1 - x0, y1 - y0);
      gl.uniform4f(u('uCase'), x, y, k, ch);
      gl.uniform4f(u('uBoite'), r.left - z.x, r.top - z.y, z.w, z.h);
      gl.uniform2f(u('uTaille'), r.width, r.height);
      gl.uniform1f(u('uRayon'), Math.min(c.rayon, r.width / 2, r.height / 2));
      gl.uniform1f(u('uOpacite'), o);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      if (!el.hasAttribute('data-sdm-verre-gl')) el.setAttribute('data-sdm-verre-gl', '');
    }
    gl.disable(gl.SCISSOR_TEST);
    gl.bindTexture(gl.TEXTURE_2D, null);
  };

  // une image de l'écran tant qu'une case y est (la page défile, l'îlot monte, une case apparaît : le verre suit sans
  // attendre) ; rien n'est dessiné si rien n'a bougé
  const boucle = () => {
    C.raf = 0;
    if (C.perdu || ![...casesVues.values()].some((c) => c.visible)) return;
    C.raf = requestAnimationFrame(boucle);
    dessiner();
  };
  const relancer = () => {
    if (!C.raf && !C.perdu) C.raf = requestAnimationFrame(boucle);
  };
  C.io = new IntersectionObserver((entrees) => {
    for (const en of entrees) {
      const c = casesVues.get(en.target);
      if (c) c.visible = en.isIntersecting;
    }
    relancer();
  });
  // l'arrondi d'une case, lu à chaque changement de taille
  C.ro = new ResizeObserver((entrees) => {
    for (const en of entrees) {
      const c = casesVues.get(en.target);
      if (c) c.rayon = parseFloat(getComputedStyle(en.target).borderTopLeftRadius) || 0;
    }
    C.cle = '';
    relancer();
  });
  const ro = new ResizeObserver(() => {
    dimensionner();
    relancer();
  });
  ro.observe(hote);
  const surResize = () => {
    dimensionner();
    relancer();
  };
  addEventListener('resize', surResize);
  fond.suiveurs.add(relancer);
  document.addEventListener('sdm:fiche-close', relancer);
  dimensionner();
  const nettoyer = () => {
    fond.suiveurs.delete(relancer);
    removeEventListener('resize', surResize);
    document.removeEventListener('sdm:fiche-close', relancer);
    C.io.disconnect();
    C.ro.disconnect();
    ro.disconnect();
    cancelAnimationFrame(C.raf);
  };
  fond.nettoyage.push(() => {
    nettoyer();
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    canvas.remove();
    casesVues.clear();
    casesCalque = null;
  });
  // contexte perdu : les cases reprennent le verre de l'expérience
  canvas.addEventListener('webglcontextlost', (e) => {
    e.preventDefault();
    C.perdu = true;
    nettoyer();
    canvas.remove();
    for (const el of casesVues.keys()) {
      el.removeAttribute('data-sdm-verre-gl');
      verres.delete(el);
    }
    casesVues.clear();
    casesCalque = null;
    verrer(portee);
  });
  return C;
}

/* ---------- l'ouverture « 8 à plat » ---------- */

// En option, en tête de l'îlot ([data-sdm-huit]), un trompe-l'œil : le labyrinthe du site, rendu par son moteur ; au
// défilement, la caméra se relève — au cœur des boucles, les deux logos (assets/sdm-chrome-huit.js). Le module, et
// le moteur qu'il démarre, partent à deux écrans de l'ouverture — pas pendant l'intro de l'en-tête. Sans WebGL 2 ou si
// le chargement échoue : les logos, côte à côte, sans course.
function huit(portee, fond) {
  const el = portee.querySelector('[data-sdm-huit]');
  if (!el || el.dataset.pret !== undefined) return;
  el.dataset.pret = '';
  const repli = () => el.classList.add('sdm-huit--repli');
  if (!el.dataset.module) return repli();
  // Au-delà de 990 px, c'est .page-wrapper qui défile (Horizon), pas la fenêtre : une marge posée sur la fenêtre ne
  // passe pas ce conteneur, qui rogne la section tant qu'elle n'est pas à l'écran — le module partait à l'arrivée
  // de l'ouverture, pas deux écrans avant (25/10). L'observateur prend alors le conteneur pour référence.
  const wrapper = document.querySelector('.page-wrapper');
  const root = wrapper && wrapper.scrollHeight > wrapper.clientHeight && el.closest('.page-wrapper') ? wrapper : null;
  const io = new IntersectionObserver(
    ([en]) => {
      if (!en.isIntersecting) return;
      io.disconnect();
      import(el.dataset.module)
        .then(({ monter }) => monter(el, fond))
        .then((s) => {
          fond.couvert = s.couvre;
          fond.nettoyage.push(s.detruire);
        })
        .catch((err) => {
          console.warn('[sdm] 8 à plat indisponible', err);
          repli();
        });
    },
    { root, rootMargin: '200% 0px' },
  );
  io.observe(el);
  fond.nettoyage.push(() => io.disconnect());
}

/* ---------- l'îlot sombre en haut de l'écran ---------- */

// Sur iPhone, le flou d'un verre coupé par le haut de l'écran lit aussi ce qui est au-dessus, hors de l'écran, où
// Safari met la couleur de fond de la page — le blanc du thème : les cases y blanchissent. Tant que les pièces d'un
// îlot sombre (ou le texte et les boutons sous elles) occupent le haut de l'écran, le fond de la page prend sa couleur
// (assets/sdm-chrome.css, .sdm-chrome-sommet) ; pas sur la tête du cartel, blanche.
const sommets = new Set();
let sommetIO = null;

function sommet(ilot) {
  if (!ilot) return;
  sommetIO ??= new IntersectionObserver(
    (entrees) => {
      for (const en of entrees) en.isIntersecting ? sommets.add(en.target) : sommets.delete(en.target);
      document.documentElement.classList.toggle('sdm-chrome-sommet', sommets.size > 0);
    },
    { rootMargin: '0px 0px -92% 0px' },
  );
  sommetIO.observe(ilot);
}

/* ---------- la fenêtre d'une pièce (îlot Chrome) ---------- */

// Au clic sur une pièce de l'îlot, une fenêtre s'ouvre comme le menu burger (sections/sdm-burger.liquid) : un voile
// flou sur la page, une carte de verre liquide qui monte du bas, un bouton rond pour la refermer — Échap et le voile
// aussi. Elle est posée une fois, à la fin de la page, hors de l'îlot (qui isole ses calques : l'en-tête et le menu
// passeraient devant). Son contenu est celui que la section a écrit dans le <template data-sdm-pop> de la pièce :
// ses vues numérotées (1 / 2), désignation, prix, description, bouton et, en option, le lien souligné vers la seconde
// page, « Être informé ». Le bouton referme la fenêtre et rejoue le clic sur la case, sans elle : la fiche s'ouvre
// comme d'habitude (le calque de sdm-fiche.js et son vol depuis la case, ou la page produit). Un clic avec Ctrl, Cmd
// ou Maj garde son effet (un nouvel onglet).
const fenetre = { el: null, carte: null, contenu: null, piece: null, passe: false, vue: 0, clavier: false };
const clicSimple = (e) => e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
const SOURIS = matchMedia('(hover: hover) and (pointer: fine)');

function pieces(portee) {
  if (!portee.querySelector('.sdm-piece > template[data-sdm-pop]')) return;
  portee.addEventListener('click', (e) => {
    if (fenetre.passe || e.defaultPrevented || !clicSimple(e)) return;
    const piece = e.target.closest('.sdm-piece');
    const modele = piece?.querySelector(':scope > template[data-sdm-pop]');
    if (!modele) return;
    e.preventDefault();
    // ouverte au clavier (un clic sans pointeur), la fenêtre rendra le focus à la case avec son anneau ; au doigt ou à
    // la souris, sans (Safari l'afficherait sur un focus rendu par le script)
    fenetre.clavier = e.detail === 0;
    ouvrirPiece(piece, modele);
  });
}

// L'îlot au teaser (snippets/sdm-teaser.liquid) : le compte à rebours, tenu à la seconde — la page a pu être servie
// depuis le cache, ou rester ouverte : l'heure de l'appareil fait foi, comparée à la date de sortie (data-fin) —, puis
// le texte de fin, l'heure venue. Ses chiffres sont le masque du calque inversé (--sdm-compte-forme), redessiné à
// chaque seconde. Et l'inscription, envoyée sans quitter la page, comme celle de la fenêtre des pièces.
function teaser(portee) {
  const compte = portee.querySelector('[data-sdm-compte]');
  const form = portee.querySelector('.sdm-teaser__formulaire');
  if (form) {
    form.submit = () => informer(form);
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      if (!form.dataset.hcaptchaBound) informerApres(form);
    });
  }
  if (!compte || compte.hidden) return;
  const fin = Number(compte.dataset.fin) * 1000;
  const calque = compte.querySelector('.sdm-compte__liquide');
  const deux = (n) => String(n).padStart(2, '0');
  const tic = () => {
    if (!compte.isConnected) return;
    const reste = Math.max(0, Math.floor((fin - Date.now()) / 1000));
    const forme = formeCompte([Math.floor(reste / 86400), Math.floor(reste / 3600) % 24, Math.floor(reste / 60) % 60, reste % 60].map(deux));
    calque?.style.setProperty('--sdm-compte-forme', `url("data:image/svg+xml,${encodeURIComponent(forme)}")`);
    if (reste > 0) return setTimeout(tic, 1000 - (Date.now() % 1000) + 5);
    compte.hidden = true;
    portee.querySelector('[data-sdm-compte-avant]')?.setAttribute('hidden', '');
    portee.querySelector('[data-sdm-compte-fin]')?.removeAttribute('hidden');
  };
  tic();
}

// le dessin du masque : 4 cases de 100 × 100, les chiffres centrés, deux points entre elles (le même que celui du
// snippet) ; cent jours et plus : trois chiffres, serrés dans la place de deux
function formeCompte(valeurs) {
  const textes = valeurs
    .map((v, i) => `<text x="${50 + 100 * i}" y="80"${v.length > 2 ? ' textLength="90" lengthAdjust="spacingAndGlyphs"' : ''}>${v}</text>`)
    .join('');
  const points = [100, 200, 300].map((x) => `<rect x="${x - 2}" y="40" width="4" height="4"/><rect x="${x - 2}" y="62" width="4" height="4"/>`).join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 100"><g font-family="Helvetica Neue,Helvetica,Arial,sans-serif" font-weight="700" font-size="74" letter-spacing="-1.5" text-anchor="middle" style="font-variant-numeric:tabular-nums">${textes}${points}</g></svg>`;
}

function poserFenetre() {
  if (fenetre.el) return fenetre;
  const el = document.createElement('div');
  el.className = 'sdm-pop';
  el.inert = true;
  el.innerHTML = `<div class="sdm-pop__voile" data-sdm-pop-fermer></div>
    <div class="sdm-pop__cale">
      <div class="sdm-pop__carte sdm-pop__verre" role="dialog" aria-modal="true" tabindex="-1">
        <div class="sdm-pop__defile"></div>
      </div>
      <button type="button" class="sdm-pop__fermer sdm-pop__verre" data-sdm-pop-fermer aria-label="Fermer">
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
          <path d="M5 5l14 14M19 5L5 19" fill="none" stroke="currentColor" stroke-width="1.2"/>
        </svg>
      </button>
    </div>
    <svg width="0" height="0" class="sdm-pop__defs" aria-hidden="true"><defs></defs></svg>`;
  document.body.append(el);
  Object.assign(fenetre, { el, carte: el.querySelector('.sdm-pop__carte'), contenu: el.querySelector('.sdm-pop__defile') });

  el.addEventListener('click', (e) => {
    if (e.target.closest('[data-sdm-pop-fermer]')) return fermerPiece();
    const fleche = e.target.closest('[data-sdm-pop-vue]');
    if (fleche) return montrerVue(fenetre.vue + Number(fleche.dataset.sdmPopVue));
    const aller = e.target.closest('[data-sdm-pop-aller]');
    if (aller) return allerPage(aller.dataset.sdmPopAller);
    if (!e.target.closest('[data-sdm-pop-bouton]') || !clicSimple(e)) return;
    e.preventDefault();
    const piece = fenetre.piece;
    fermerPiece(false);
    fenetre.passe = true;
    piece?.click();
    fenetre.passe = false;
  });
  el.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form.matches('.sdm-pop__formulaire')) return;
    e.preventDefault();
    // hCaptcha de Shopify branché : son propre écouteur lui a déjà demandé un jeton (voir informer)
    if (!form.dataset.hcaptchaBound) informerApres(form);
  });
  el.addEventListener('keydown', (e) => {
    if (!el.hasAttribute('open')) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      fermerPiece();
    } else if (e.key === 'Tab') garderFocus(e);
    else if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && !e.target.matches('input')) {
      montrerVue(fenetre.vue + (e.key === 'ArrowRight' ? 1 : -1));
    }
    e.stopPropagation(); // l'expérience (le labyrinthe) écoute aussi le clavier, et la fenêtre a un champ
  });
  // comme le menu burger : le défilement reste dans la fenêtre, sans atteindre l'expérience ni la page
  for (const type of ['touchstart', 'touchmove', 'wheel']) {
    el.addEventListener(type, (e) => e.stopPropagation(), { passive: true });
  }
  el.querySelector('.sdm-pop__voile').addEventListener('touchmove', (e) => e.preventDefault(), { passive: false });

  // le verre liquide du menu burger sur la carte et le bouton ; sans lui, le verre garde son flou, son reflet et son
  // liseré en CSS
  import('sdm/glass')
    .then(({ createGlass, GLASS_DEFAULTS }) => {
      const verre = createGlass({
        defs: el.querySelector('defs'),
        params: { ...structuredClone(GLASS_DEFAULTS), ...VERRES.fenetre },
      });
      el.querySelectorAll('.sdm-pop__verre').forEach(verre.register);
    })
    .catch(() => {});
  return fenetre;
}

function ouvrirPiece(piece, modele) {
  const { el, carte, contenu } = poserFenetre();
  fenetre.piece = piece;
  fenetre.vue = 0;
  contenu.replaceChildren(modele.content.cloneNode(true));
  contenu.style.minHeight = '';
  delete contenu.dataset.page;
  contenu.scrollTop = 0;
  const titre = contenu.querySelector('[data-sdm-pop-titre]');
  if (titre) {
    titre.id = 'sdm-pop-titre';
    carte.setAttribute('aria-labelledby', titre.id);
  }
  contenu.querySelector('[data-sdm-pop-vues]')?.addEventListener('scroll', suivreVue, { passive: true });
  const formulaire = contenu.querySelector('.sdm-pop__formulaire');
  if (formulaire) formulaire.submit = () => informer(formulaire);
  el.dataset.ton = piece.closest('.sdm-chrome--sombre') ? 'sombre' : 'clair';
  el.style.setProperty('--sdm-pop-barre', `${barreApercu()}px`);
  el.inert = false;
  el.setAttribute('open', '');
  document.documentElement.classList.add('sdm-pop-open');
  carte.focus({ preventScroll: true });
}

// Dans l'aperçu d'un thème, la barre de Shopify est fixée en bas de l'écran, par-dessus la page : la fenêtre monte
// d'autant, pour que son bas et son bouton rond restent visibles. Hors aperçu (la boutique en ligne), 0.
function barreApercu() {
  const r = document.querySelector('#preview-bar-iframe, #PBarNextFrameWrapper')?.getBoundingClientRect();
  return r && r.height > 0 && r.height < 200 && r.bottom >= innerHeight - 2 ? Math.round(innerHeight - r.top) : 0;
}

function fermerPiece(rendreFocus = true) {
  const { el, piece } = fenetre;
  if (!el?.hasAttribute('open')) return;
  el.removeAttribute('open');
  el.inert = true;
  document.documentElement.classList.remove('sdm-pop-open');
  if (!rendreFocus || !piece) return;
  if (!fenetre.clavier) {
    piece.dataset.sdmSansAnneau = '';
    piece.addEventListener('blur', () => delete piece.dataset.sdmSansAnneau, { once: true });
  }
  piece.focus({ preventScroll: true, focusVisible: fenetre.clavier });
}

// les vues : la galerie glisse au cran ; le numéro (1 / 2) et les flèches suivent — tout de suite au clic, au cran
// atteint quand le doigt la fait glisser
function montrerVue(i) {
  const vues = fenetre.contenu.querySelector('[data-sdm-pop-vues]');
  const n = vues?.children.length ?? 0;
  if (n < 2 || vues.closest('[hidden]')) return;
  i = borne(i, 0, n - 1);
  marquerVue(i, n);
  vues.scrollTo({ left: i * vues.clientWidth, behavior: REDUIT ? 'auto' : 'smooth' });
}

function suivreVue(e) {
  const vues = e.currentTarget;
  const largeur = Math.max(1, vues.clientWidth);
  if (vues.scrollLeft % largeur < 2) marquerVue(Math.round(vues.scrollLeft / largeur), vues.children.length);
}

function marquerVue(i, n) {
  const { contenu } = fenetre;
  fenetre.vue = i;
  const numero = contenu.querySelector('[data-sdm-pop-numero]');
  if (numero) numero.textContent = i + 1;
  const avant = contenu.querySelector('[data-sdm-pop-vue="-1"]');
  const apres = contenu.querySelector('[data-sdm-pop-vue="1"]');
  const actif = document.activeElement;
  avant?.toggleAttribute('disabled', i === 0);
  apres?.toggleAttribute('disabled', i === n - 1);
  // la flèche qu'on vient de presser s'efface au bout de la galerie : le focus passe à l'autre
  if (actif?.disabled) (actif === avant ? apres : avant)?.focus({ preventScroll: true });
}

// la seconde page (« Être informé ») prend la place de la première, dans la même carte, sans qu'elle rapetisse
function allerPage(nom) {
  const { contenu } = fenetre;
  const page = contenu.querySelector(`[data-sdm-pop-page="${nom}"]`);
  if (!page?.hidden) return;
  contenu.style.minHeight = `${Math.max(parseFloat(contenu.style.minHeight) || 0, contenu.offsetHeight)}px`;
  contenu.querySelectorAll('[data-sdm-pop-page]').forEach((p) => (p.hidden = p !== page));
  contenu.dataset.page = nom;
  contenu.scrollTop = 0;
  // au clavier ou à la souris, le champ tout de suite ; au doigt, le titre (le clavier ne surgit pas sans qu'on le
  // demande) ; au retour, le lien d'où l'on venait
  const cible =
    nom === 'informer'
      ? page.querySelector(SOURIS.matches ? 'input[type="email"]' : '[data-sdm-pop-titre-informer]')
      : contenu.querySelector('[data-sdm-pop-aller="informer"]');
  cible?.focus({ preventScroll: true });
}

// L'inscription part sans quitter la fenêtre (le formulaire client de Shopify, étiqueté « newsletter » : l'e-mail
// rejoint les abonnés). Shopify répond par la page d'où il vient, « ?customer_posted=true » à l'adresse quand c'est
// fait. Son anti-robot (hCaptcha, invisible le plus souvent) se branche sur le formulaire au premier focus : il garde
// la fonction submit du formulaire, demande un jeton à l'envoi, puis l'appelle — la nôtre, posée à l'ouverture de la
// fenêtre (ouvrirPiece) ou au teaser (teaser) : l'envoi part avec son jeton. Si Shopify veut tout de même vérifier sur sa page /challenge,
// le formulaire part pour de bon et le visiteur y passe.
async function informer(form) {
  if (form.hasAttribute('aria-busy')) return;
  const etat = form.querySelector('[data-sdm-pop-etat]');
  form.setAttribute('aria-busy', 'true');
  etat.textContent = '';
  try {
    const r = await fetch(form.action, { method: 'POST', body: new FormData(form) });
    if (new URL(r.url).pathname.startsWith('/challenge')) return HTMLFormElement.prototype.submit.call(form);
    if (!r.ok || !r.url.includes('customer_posted=true')) throw new Error(`réponse ${r.status}`);
    form.dataset.envoye = '';
    etat.textContent = etat.dataset.merci;
    // le bouton pressé disparaît avec le formulaire : le focus passe au titre de la page
    if (!document.activeElement || document.activeElement === document.body || form.contains(document.activeElement)) {
      form.closest('[data-sdm-pop-page]')?.querySelector('[data-sdm-pop-titre-informer]')?.focus({ preventScroll: true });
    }
  } catch {
    etat.textContent = etat.dataset.erreur;
  } finally {
    form.removeAttribute('aria-busy');
  }
}

// envoi avant que l'anti-robot soit branché (il se charge encore) : on l'attend quelques secondes, puis on envoie
// sans lui — et sans anti-robot du tout si la boutique ne l'a pas activé
const attentes = new WeakSet();
async function informerApres(form) {
  if (attentes.has(form)) return;
  attentes.add(form);
  for (let t = 0; form.dataset.cptcha && !form.dataset.hcaptchaBound && t < 40; t++) {
    await new Promise((fin) => setTimeout(fin, 100));
  }
  attentes.delete(form);
  form.submit();
}

// le focus reste dans la fenêtre, comme dans le menu burger
function garderFocus(e) {
  const pris = [...fenetre.el.querySelectorAll('.sdm-pop__carte :is(a[href], button, input), .sdm-pop__fermer')].filter(
    (n) => !n.disabled && !n.closest('[hidden]'),
  );
  const actif = document.activeElement;
  if (!pris.length) return;
  if (e.shiftKey && (actif === pris[0] || actif === fenetre.carte)) {
    e.preventDefault();
    pris.at(-1).focus();
  } else if (!e.shiftKey && actif === pris.at(-1)) {
    e.preventDefault();
    pris[0].focus();
  }
}

/* ---------- le bouton chromé du cartel (en option) : une pilule de chrome liquide ---------- */

// Chaque bouton a sa toile et son contexte WebGL ; une seule boucle les anime tous, tant qu'un bouton est à l'écran :
// 30 images par seconde quand le visiteur bouge, 10 sinon. Le liquide de leur surface suit la même vitesse que le
// fond : presque arrêté au repos.
const boutons = [];
const anim = { raf: 0, derniere: 0, temps: 0 };
const chromes = new WeakSet();
const borne = (x, a, b) => Math.min(b, Math.max(a, x));

function animer(maintenant) {
  anim.raf = 0;
  const actifs = boutons.filter((b) => b.visible && !masque(b.el));
  if (!actifs.length) return;
  if (!REDUIT) anim.raf = requestAnimationFrame(animer);
  const e = lireElan(maintenant);
  if (anim.derniere && maintenant - anim.derniere < (calme(e) ? REPOS_MS : IMAGE_MS) - 2) return;
  anim.temps += anim.derniere ? Math.min(0.1, (maintenant - anim.derniere) / 1000) * vitesse(e) : 0;
  anim.derniere = maintenant;
  actifs.forEach((b) => peindre(b));
}

function relancerBoutons() {
  if (!anim.raf) anim.raf = requestAnimationFrame(animer);
}
document.addEventListener('sdm:fiche-close', relancerBoutons);

function peindre(b, immediat = false) {
  const { el, toile, gl, prog } = b;
  const w = el.offsetWidth;
  const h = el.offsetHeight;
  if (!w || !h || gl.isContextLost()) return;
  const e = Math.min(devicePixelRatio || 1, 2);
  const W = Math.round(w * e);
  const H = Math.round(h * e);
  if (toile.width !== W || toile.height !== H) Object.assign(toile, { width: W, height: H });

  // le pointeur : à moins de 160 px du bouton, il le tourne un peu vers lui ; le défilement fait glisser le studio
  const r = el.getBoundingClientRect();
  let incl = [0, 0];
  if (!REDUIT && pointeur.quand > -Infinity) {
    const ex = Math.max(r.left - pointeur.x, 0, pointeur.x - r.right);
    const ey = Math.max(r.top - pointeur.y, 0, pointeur.y - r.bottom);
    const proche = borne(1 - Math.hypot(ex, ey) / 160, 0, 1);
    incl = [
      borne((pointeur.x - (r.left + r.width / 2)) / (r.width / 2), -1, 1) * proche,
      borne((r.top + r.height / 2 - pointeur.y) / (r.height / 2 + 40), -1, 1) * proche,
    ];
  }
  const k = immediat || REDUIT ? 1 : 0.15;
  b.incl[0] += (incl[0] - b.incl[0]) * k;
  b.incl[1] += (incl[1] - b.incl[1]) * k;
  const vue = REDUIT ? 0 : borne((0.5 - (r.top + r.height / 2) / innerHeight) * 0.16, -0.12, 0.12);

  gl.viewport(0, 0, W, H);
  gl.uniform2f(prog.u('uTaille'), w, h);
  gl.uniform1f(prog.u('uEchelle'), e);
  gl.uniform1f(prog.u('uTemps'), anim.temps + 4);
  gl.uniform2f(prog.u('uInclinaison'), b.incl[0], b.incl[1]);
  gl.uniform1f(prog.u('uVue'), vue);
  gl.drawArrays(gl.TRIANGLES, 0, 3);
  el.classList.add('is-metal');
}

/**
 * Pose le chrome sur un bouton : une toile sous son texte. Si le texte du bouton est réécrit (textContent), ce qui
 * retire la toile, elle est remise aussitôt, telle quelle. Sans WebGL, le dégradé de assets/sdm-chrome.css reste.
 */
function chromer(el) {
  if (chromes.has(el)) return;
  const toile = document.createElement('canvas');
  const gl = contexte(toile, { alpha: false, antialias: false, depth: false, stencil: false });
  const prog = gl && programme(gl, METAL);
  if (!prog) return;
  chromes.add(el);
  toile.className = 'sdm-chrome-metal';
  toile.setAttribute('aria-hidden', 'true');
  el.prepend(toile);
  const b = {
    el,
    toile,
    gl,
    prog,
    visible: false,
    incl: [0, 0],
    observateurs: [
      new MutationObserver(() => toile.parentNode !== el && el.prepend(toile)),
      new IntersectionObserver(([e]) => {
        b.visible = e.isIntersecting;
        if (b.visible) {
          peindre(b, true);
          relancerBoutons();
        }
      }),
      new ResizeObserver(() => b.visible && peindre(b, true)),
    ],
  };
  b.observateurs[0].observe(el, { childList: true });
  b.observateurs[1].observe(el);
  b.observateurs[2].observe(el);
  // les boutons sortis de la page sont oubliés
  for (let i = boutons.length - 1; i >= 0; i--) {
    const autre = boutons[i];
    if (autre.el.isConnected) continue;
    autre.observateurs.forEach((o) => o.disconnect());
    autre.gl.getExtension('WEBGL_lose_context')?.loseContext();
    chromes.delete(autre.el);
    autre.toile.remove();
    boutons.splice(i, 1);
  }
  boutons.push(b);
}

/* ---------- le 8 de la fiche Chrome : une matière en fusion ---------- */

// Le 8 aux deux bords de la fiche Chrome (snippets/sdm-huit-cotes.liquid) est d'abord en CSS : sa matière, son halo et
// la lumière qui le parcourt (assets/sdm-chrome.css). Ici, la même image redessinée en WebGL — une toile par côté,
// posée sur les calques CSS —, qui coule comme un métal en fusion. Le halo et la lumière sont refaits dans le même
// dessin, pour suivre la forme. L'amplitude vient du réglage du thème (data-deformation, de 0 à 10) ; à 0, en
// mouvement réduit ou sans WebGL, les calques CSS restent seuls.
//
// Le « magma » (retour du 27/10), d'après des techniques publiées :
// - le courant est le rotationnel d'un potentiel (« curl noise », Bridson, Hourihan et Nordenstam, SIGGRAPH 2007) :
//   un écoulement incompressible, qui fait couler la matière sans la tasser ni la déchirer ;
// - ce potentiel suit le « flow noise » (Perlin et Neyret, SIGGRAPH 2001 — leurs exemples : la lave) : une grande
//   échelle q emporte la petite (pseudo-advection), et q replie aussi le tout (le « domain warping » d'Inigo Quilez) :
//   le contour s'affaisse et ondule lentement, comme une matière lourde ;
// - l'incandescence : de larges zones chaudes, tirées du même champ, qui dérivent avec lui ; le métal y monte vers le
//   blanc et sa lueur s'y renforce ; ailleurs il refroidit, un rien plus sombre (la croûte) ;
// - l'inertie : au défilement, le milieu de chaque boucle s'affaisse avec la page et revient lentement (un ressort
//   lourd, tenu par le module) ; une loupe liquide sous le pointeur ; un reflet sur un relief tiré de la silhouette.
// Le défilement entraîne aussi le courant le long du 8. Rien ne bouge au bord de l'écran, où le 8 continue.
//
// Deux passes par image : le champ (le courant et la chaleur, HUIT_CHAMP) dans une texture au quart de la toile — il
// est lisse, et c'est le calcul coûteux —, puis l'image (HUIT), qui le lit. Bruit de simplex : Ian McEwan et Stefan
// Gustavson (Ashima Arts, licence MIT), repris dans « Tiling Simplex Noise and Flow Noise » (Gustavson et McEwan,
// JCGT 2022).
const HUIT_CHAMP = `precision highp float;
uniform vec2 uTaille;
uniform float uTemps;
uniform float uPhase;
uniform float uFlux;
varying vec2 vUv;
vec4 permute(vec4 x) { return mod((x * 34.0 + 1.0) * x, 289.0); }
float snoise(vec3 v) {
  const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
  vec3 i = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);
  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);
  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;
  i = mod(i, 289.0);
  vec4 p = permute(permute(permute(i.z + vec4(0.0, i1.z, i2.z, 1.0)) + i.y + vec4(0.0, i1.y, i2.y, 1.0)) + i.x + vec4(0.0, i1.x, i2.x, 1.0));
  vec3 ns = (1.0 / 7.0) * D.wyz - D.xzx;
  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);
  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);
  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);
  vec4 s0 = floor(b0) * 2.0 + 1.0;
  vec4 s1 = floor(b1) * 2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));
  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;
  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);
  vec4 nm = 1.79284291400159 - 0.85373472095314 * vec4(dot(p0, p0), dot(p1, p1), dot(p2, p2), dot(p3, p3));
  p0 *= nm.x;
  p1 *= nm.y;
  p2 *= nm.z;
  p3 *= nm.w;
  vec4 m = max(0.6 - vec4(dot(x0, x0), dot(x1, x1), dot(x2, x2), dot(x3, x3)), 0.0);
  m = m * m;
  return 42.0 * dot(m * m, vec4(dot(p0, x0), dot(p1, x1), dot(p2, x2), dot(p3, x3)));
}
vec2 grand(vec2 p, float t) {
  return vec2(snoise(vec3(p * 1.1, t * 0.5)), snoise(vec3(p * 1.1 + vec2(5.2, 1.3), t * 0.5)));
}
float potentiel(vec2 p, vec2 q, float t) {
  return snoise(vec3(p * vec2(1.3, 1.0) + 0.9 * q, t * 0.7)) + 0.18 * snoise(vec3(p * 3.1 + 1.8 * q, t * 1.1));
}
void main() {
  vec2 uv = vec2(vUv.x, 1.0 - vUv.y);
  vec2 p = uv * vec2(uTaille.x / uTaille.y, 1.0) + vec2(0.0, uFlux);
  float t = uTemps + uPhase;
  vec2 q = grand(p, t);
  float e = 0.015;
  float c0 = potentiel(p, q, t);
  vec2 d = vec2(potentiel(p + vec2(0.0, e), grand(p + vec2(0.0, e), t), t) - c0,
                c0 - potentiel(p + vec2(e, 0.0), grand(p + vec2(e, 0.0), t), t)) / e * 0.3;
  float chaleur = smoothstep(0.05, 0.75, snoise(vec3(p * vec2(1.2, 0.8) + 1.2 * q, t * 0.35)) * 0.5 + 0.5 + 0.25 * c0);
  gl_FragColor = vec4(clamp(d / 3.0 + 0.5, 0.0, 1.0), chaleur, 1.0);
}`;

// L'image : le courant et la chaleur lus dans le champ ; le ressort et la loupe ; la matière et le halo lus au point
// déplacé ; l'incandescence ; le reflet ; puis, comme en CSS : le halo (aplat de lueur sous la silhouette floutée,
// 12 %, plus fort où c'est chaud), la matière fondue en « screen » sur le noir de la fiche, la lumière qui avive la
// matière à sa mesure (90 %) et son auréole sous le halo (90 % × 75 %).
const HUIT = `precision highp float;
uniform sampler2D uMatiere;
uniform sampler2D uHalo;
uniform sampler2D uChamp;
uniform vec2 uTaille;
uniform float uAmp;
uniform float uRessort;
uniform vec3 uPointeur;
uniform vec2 uLumiere;
uniform vec3 uFond;
uniform vec3 uLueur;
uniform float uLueurOn;
uniform float uBande;
varying vec2 vUv;
void main() {
  vec2 uv = vec2(vUv.x, 1.0 - vUv.y);
  float r = uTaille.x / uTaille.y;
  vec4 f = texture2D(uChamp, vUv);
  vec2 d = (f.xy - 0.5) * 3.0;
  float chaleur = f.z;
  vec2 px = d * uAmp;
  float boucle = sin(6.2831853 * uv.y);
  px.y -= uRessort * boucle * boucle;
  px.x += abs(uRessort) * 0.3 * boucle;
  vec2 rv = (uv - uPointeur.xy) * vec2(r, 1.0);
  px -= rv * uTaille.y * 0.22 * exp(-dot(rv, rv) / 0.014) * uPointeur.z;
  px *= smoothstep(0.0, 0.3, uv.x);
  vec2 st = uv + px / uTaille;
  float m = texture2D(uMatiere, st).r;
  float h = texture2D(uHalo, st).a;
  float metal = m + 0.55 * chaleur * (1.0 - m) * smoothstep(0.04, 0.45, m) * 0.8;
  metal *= mix(1.0, 0.8, 0.6 * (1.0 - chaleur) * smoothstep(0.3, 0.9, m));
  vec2 k = vec2(1.5 / 82.0, 1.5 / 487.0);
  vec2 pente = vec2(texture2D(uHalo, st + vec2(k.x, 0.0)).a - texture2D(uHalo, st - vec2(k.x, 0.0)).a,
                    texture2D(uHalo, st + vec2(0.0, k.y)).a - texture2D(uHalo, st - vec2(0.0, k.y)).a);
  vec3 n = normalize(vec3(-pente * 7.0 + d * 0.45, 1.0));
  float reflet = pow(max(dot(n, normalize(normalize(vec3(uLumiere, 1.0)) + vec3(0.0, 0.0, 1.0))), 0.0), 28.0);
  float b = uLueurOn * (1.0 - smoothstep(0.0, 0.2, abs(uv.y - uBande)));
  vec3 c = mix(uFond, uLueur, (0.12 + 0.165 * chaleur) * h * uLueurOn);
  c = 1.0 - (1.0 - c) * (1.0 - clamp(metal, 0.0, 1.0));
  c = mix(c, uLueur, clamp(reflet * smoothstep(0.08, 0.5, m), 0.0, 1.0));
  c = mix(c, uLueur, 0.9 * b * m);
  c = mix(c, uLueur, 0.675 * b * h);
  c += (fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453) - 0.5) / 255.0;
  gl_FragColor = vec4(c, 1.0);
}`;

// La lumière, au rythme de celle du CSS (sdm-huit-lumiere : 12 s, cubic-bezier(0.45, 0, 0.55, 1), ici sa sinusoïde) :
// le centre de la bande, en hauteur de toile depuis le haut — à gauche, elle descend pendant la première moitié du
// tour ; à droite, elle remonte pendant la seconde ; le reste du temps, hors de la toile.
const TOUR_MS = 12000;
function bande(ms, droite) {
  const p = (((ms % TOUR_MS) + TOUR_MS) % TOUR_MS) / TOUR_MS;
  if (droite ? p < 0.5 : p >= 0.5) return -10;
  const x = droite ? (1 - p) * 2 : p * 2;
  return -0.2 + 1.4 * (0.5 - 0.5 * Math.cos(Math.PI * x));
}

function textureHuit(gl, unite, img) {
  gl.activeTexture(gl.TEXTURE0 + unite);
  gl.bindTexture(gl.TEXTURE_2D, gl.createTexture());
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
}

// « rgb(0, 0, 0) » ou « 255 255 255 » (une variable CSS) en RVB de 0 à 1
const rvbCss = (s) => (String(s).match(/[\d.]+/g) || [0, 0, 0]).slice(0, 3).map((n) => Number(n) / 255);

/**
 * Une image par tranche de 33 ms tant que la lumière passe, que le visiteur bouge ou que le ressort oscille, une par
 * tranche de 100 ms sinon ; rien hors de l'écran ni sous la fiche ouverte en calque. Le défilement se lit à la place
 * de l'élément dans l'écran (il couvre la fiche) : page produit ou calque, quel que soit ce qui défile. Les toiles
 * apparaissent en fondu sur les calques CSS, qui s'effacent ensuite ; si le contexte WebGL est perdu, elles partent et
 * les calques CSS reviennent.
 */
class SdmHuitCotes extends HTMLElement {
  #cotes = [];
  #nettoyage = [];
  #raf = 0;
  #visible = false;
  #dernier = 0;
  #temps = 0;
  #force = 0;
  #origine = 0;
  #amp = [0, 0];
  #ressortMax = 0;
  #haut = null; // la place de l'élément dans l'écran à l'image précédente (px)
  #flux = 0; // le courant entraîné par le défilement, en hauteurs de toile
  #ressort = { x: 0, v: 0 }; // le milieu des boucles : son écart (px) et sa vitesse

  connectedCallback() {
    const reglage = Number(this.dataset.deformation) || 0;
    if (REDUIT || reglage <= 0 || this.#cotes.length) return;
    // px CSS : la coulée au repos, ce que le geste le plus franc y ajoute, et l'affaissement au défilement
    this.#amp = [0.6 * reglage, 0.6 * reglage];
    this.#ressortMax = 1.6 * reglage;
    this.#demarrer().catch((err) => console.warn('[sdm] 8 de la fiche :', err));
  }

  disconnectedCallback() {
    this.#arreter();
  }

  #arreter() {
    this.#nettoyage.forEach((f) => f());
    this.#nettoyage = [];
    cancelAnimationFrame(this.#raf);
    this.#raf = 0;
    this.#cotes.forEach((c) => {
      c.gl.getExtension('WEBGL_lose_context')?.loseContext();
      c.toile.remove();
    });
    this.#cotes = [];
    this.classList.remove('is-gl', 'is-gl-seul');
  }

  async #demarrer() {
    const charger = (src) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = src;
      return img.decode().then(() => img);
    };
    const [matiere, halo] = await Promise.all([charger(this.dataset.matiere), charger(this.dataset.halo)]);
    if (!this.isConnected || this.#cotes.length) return;

    const lueur = this.hasAttribute('data-lueur');
    const teinte = rvbCss(getComputedStyle(this).getPropertyValue('--sdm-fc-lueur') || '255 255 255');
    for (const el of this.querySelectorAll('.sdm-huit-cote')) {
      const toile = document.createElement('canvas');
      toile.className = 'sdm-huit-cote__toile';
      const gl = contexte(toile, { alpha: false, antialias: false, depth: false, stencil: false, powerPreference: 'low-power' });
      const champ = gl && programme(gl, HUIT_CHAMP);
      const prog = champ && programme(gl, HUIT);
      if (!prog) return this.#arreter(); // les deux côtés en WebGL, ou aucun : les calques CSS restent
      textureHuit(gl, 0, matiere);
      textureHuit(gl, 1, halo);
      const droite = el.classList.contains('sdm-huit-cote--droite');
      gl.useProgram(champ.p);
      gl.uniform1f(champ.u('uPhase'), droite ? 7.3 : 0); // les deux côtés ne coulent pas ensemble
      gl.useProgram(prog.p);
      gl.uniform1i(prog.u('uMatiere'), 0);
      gl.uniform1i(prog.u('uHalo'), 1);
      gl.uniform1i(prog.u('uChamp'), 2);
      gl.uniform3fv(prog.u('uFond'), rvbCss(getComputedStyle(el).backgroundColor));
      gl.uniform3fv(prog.u('uLueur'), teinte);
      gl.uniform1f(prog.u('uLueurOn'), lueur ? 1 : 0);
      toile.addEventListener('webglcontextlost', (e) => {
        e.preventDefault();
        this.#arreter();
      });
      el.append(toile);
      // le champ : sa texture (unité 2) et le tampon où il est dessiné, dimensionnés à la première image
      const fbo = gl.createFramebuffer();
      gl.activeTexture(gl.TEXTURE2);
      gl.bindTexture(gl.TEXTURE_2D, gl.createTexture());
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      this.#cotes.push({ el, toile, gl, prog, champ, fbo, fw: 0, fh: 0, droite, w: el.clientWidth, h: el.clientHeight, loupe: [0.5, 0.5, 0] });
    }

    // la lumière reprend où en est celle du CSS, sans saut au fondu
    const css = this.querySelector('.sdm-huit-cote__aura > span')?.getAnimations?.()[0];
    this.#origine = performance.now() - (Number(css?.currentTime) || 0);

    const ro = new ResizeObserver(() => {
      this.#cotes.forEach((c) => Object.assign(c, { w: c.el.clientWidth, h: c.el.clientHeight }));
      this.#relancer();
    });
    this.#cotes.forEach((c) => ro.observe(c.el));
    const io = new IntersectionObserver(([e]) => {
      this.#visible = e.isIntersecting;
      this.#relancer();
    });
    io.observe(this);
    const reprendre = () => this.#relancer();
    document.addEventListener('sdm:fiche-close', reprendre);
    this.#nettoyage.push(() => {
      ro.disconnect();
      io.disconnect();
      document.removeEventListener('sdm:fiche-close', reprendre);
    });
  }

  #relancer() {
    if (!this.#cotes.length || !this.#visible || this.#raf || masque(this)) return;
    this.#raf = requestAnimationFrame(this.#image);
  }

  #image = (maintenant) => {
    this.#raf = 0;
    if (!this.#cotes.length || !this.#visible || masque(this)) return;
    this.#raf = requestAnimationFrame(this.#image);
    const e = lireElan(maintenant);
    const ressort = this.#ressort;
    const oscille = Math.abs(ressort.x) > 0.05 || Math.abs(ressort.v) > 0.5;
    const lent = calme(e) && !oscille && !this.hasAttribute('data-lueur');
    if (this.#dernier && maintenant - this.#dernier < (lent ? REPOS_MS : IMAGE_MS) - MARGE_MS) return;
    const dt = this.#dernier ? Math.min(0.1, (maintenant - this.#dernier) / 1000) : 0;
    this.#dernier = maintenant;

    // le geste monte vite et retombe doucement ; il fait couler l'onde plus vite et plus fort
    const geste = Math.min(1, 0.5 * e.defil + 0.35 * e.pointeur);
    this.#force += (geste - this.#force) * (geste > this.#force ? 0.3 : 0.06);
    this.#temps += dt * (0.15 + 0.9 * this.#force);
    const amp = this.#amp[0] + this.#amp[1] * this.#force;

    // le défilement depuis l'image précédente (px, > 0 vers le bas) : il entraîne le courant le long du 8 et tire le
    // milieu des boucles, retenu par un ressort lourd et amorti (raideur 40, amortissement 0,45 : la matière
    // s'affaisse et revient lentement, sans rebondir plus d'une fois)
    const haut = this.getBoundingClientRect().top;
    const defile = this.#haut === null || !dt ? 0 : this.#haut - haut;
    this.#haut = haut;
    const hauteur = this.#cotes[0]?.h || innerHeight;
    this.#flux += (defile / hauteur) * 0.35;
    if (dt) {
      const cible = borne((-defile / dt) * 0.0006, -1, 1) * this.#ressortMax;
      const pas = Math.min(dt, 1 / 30);
      ressort.v += (40 * (cible - ressort.x) - 2 * 0.45 * Math.sqrt(40) * ressort.v) * pas;
      ressort.x += ressort.v * pas;
    }
    // la lumière qui fait briller le relief dérive avec le courant, et suit un peu le ressort
    const lumiere = [-0.35 + 0.15 * Math.sin(this.#temps * 0.7), -0.5 + ressort.x * 0.02];
    const recent = !REDUIT && maintenant - pointeur.quand < 1500;

    for (const c of this.#cotes) {
      const { gl, prog, champ, toile, w, h } = c;
      if (!w || !h || gl.isContextLost()) continue;
      // la loupe : le pointeur à moins de 200 px du côté, dans sa toile (le côté droit est en miroir)
      let loupe = [0.5, 0.5, 0];
      if (recent) {
        const r = c.el.getBoundingClientRect();
        const ecart = Math.hypot(Math.max(r.left - pointeur.x, 0, pointeur.x - r.right), Math.max(r.top - pointeur.y, 0, pointeur.y - r.bottom));
        const x = c.droite ? (r.right - pointeur.x) / r.width : (pointeur.x - r.left) / r.width;
        loupe = [x, (pointeur.y - r.top) / r.height, borne(1 - ecart / 200, 0, 1)];
      }
      c.loupe[2] += (loupe[2] - c.loupe[2]) * 0.12;
      if (loupe[2] > 0) [c.loupe[0], c.loupe[1]] = loupe;
      const k = Math.min(devicePixelRatio || 1, 2);
      const W = Math.round(w * k);
      const H = Math.round(h * k);
      if (toile.width !== W || toile.height !== H) Object.assign(toile, { width: W, height: H });
      // le champ, au quart de la toile, dans sa texture
      const fw = Math.max(1, Math.round(W / 4));
      const fh = Math.max(1, Math.round(H / 4));
      gl.activeTexture(gl.TEXTURE2);
      if (c.fw !== fw || c.fh !== fh) {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, fw, fh, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
        gl.bindFramebuffer(gl.FRAMEBUFFER, c.fbo);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, gl.getParameter(gl.TEXTURE_BINDING_2D), 0);
        Object.assign(c, { fw, fh });
      }
      gl.bindFramebuffer(gl.FRAMEBUFFER, c.fbo);
      gl.viewport(0, 0, fw, fh);
      gl.useProgram(champ.p);
      gl.uniform2f(champ.u('uTaille'), w, h);
      gl.uniform1f(champ.u('uTemps'), this.#temps);
      gl.uniform1f(champ.u('uFlux'), this.#flux);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      // puis l'image
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, W, H);
      gl.useProgram(prog.p);
      gl.uniform2f(prog.u('uTaille'), w, h);
      gl.uniform1f(prog.u('uAmp'), amp);
      gl.uniform1f(prog.u('uRessort'), ressort.x);
      gl.uniform3fv(prog.u('uPointeur'), c.loupe);
      gl.uniform2fv(prog.u('uLumiere'), lumiere);
      gl.uniform1f(prog.u('uBande'), bande(maintenant - this.#origine, c.droite));
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }
    if (!this.classList.contains('is-gl')) {
      this.classList.add('is-gl');
      const seul = setTimeout(() => this.classList.add('is-gl-seul'), 700);
      this.#nettoyage.push(() => clearTimeout(seul));
    }
  };
}

if (!customElements.get('sdm-chrome-fond')) customElements.define('sdm-chrome-fond', SdmChromeFond);
if (!customElements.get('sdm-huit-cotes')) customElements.define('sdm-huit-cotes', SdmHuitCotes);
